/**
 * Healthy Dog Tool — vanilla JS (no jQuery / Vue)
 *
 * State lives on window.healthyDogTool.state
 * Views: data-hdt-view="quiz|essentials|dosages|ordered" on .quizPersonalizeDashboard
 * Clicks: data-hdt-action on the element
 * Copy bindings: data-hdt-bind="dogname|emailaddress|..."
 * Quiz questions render from #quizStructure JSON
 */
const CHECKMARK_SRC = 'https://cdn.shopify.com/s/files/1/0316/8657/files/checkmark-green.png';
const CLOCK_SRC = 'https://cdn.shopify.com/s/files/1/0316/8657/files/clock.png';
const DOG_DEFAULT_SRC = 'https://cdn.shopify.com/s/files/1/0316/8657/files/dog-default.png?6329285897049311615';
const IFRAME_PAGE = 'https://peterdobias.com/pages/dog-app-iframe';
const EMAILJS_PUBLIC_KEY = '3Ol6m46OjE9OcpVS4';
// Gadget app proxy (see shopify.app.toml: prefix "apps", subpath "pd-gen").
const SUBSCRIBE_API_PATH = '/apps/pd-gen/healthydogtool/subscribe';
const PLACEHOLDER_ARTICLE_IMG = 'https://peterdobias.com/cdn/shop/t/517/assets/placeholder-peter_200x.jpg';
const PRODUCT_TYPE_TAGS = ['Probiotics', 'Vitamins', 'Minerals', 'Omegas', 'Greeting Cards'];

// Step URLs use a ?step= query param (same as the recipe maker) so heatmaps and GA4 can
// follow the journey. Params already on the URL - utm tags, email campaign ids, anything
// coming from other sections - are never touched, the tool only writes its own key.
const STEP_PARAM = 'step';

// Quiz steps are keyed by "<section>_<subSection>"
const QUIZ_STEP_SLUGS = {
	'0_0': 'start',
	'1_0': 'dog-health',
	'1_1': 'health-concerns',
	'1_2': 'health-challenges',
	'1_3': 'health-and-longevity',
	'2_0': 'dog-details',
	'2_1': 'quiz-end',
	'2_2': 'whats-next',
	'2_3': 'your-health-plan',
};

const VIEW_STEP_SLUGS = {
	essentials: '4-essentials',
	dosages: 'weekly-plan',
	ordered: 'thank-you',
};

// Old slugs still resolve so existing heatmap / GA sessions and bookmarks keep working
const LEGACY_STEP_SLUGS = {
	'not-sure-whats-wrong': 'health-challenges',
	newsletter: 'health-and-longevity',
	'whats-next-2': 'your-health-plan',
};

// Refreshing or landing on this step sends the visitor back to the first question
const RESTART_ON_LOAD_SLUG = 'your-health-plan';

// Details, answers and weekly-plan progress share one hard 48h window from first write.
const STORAGE_TTL_MS = 48 * 60 * 60 * 1000;
const PLAN_TTL_MS = STORAGE_TTL_MS;
const PLAN_REACHED_AT_KEY = 'pd_hdt_plan_reached_at';
const LAST_VISIT_KEY = 'pd_last_visit';
const INBOUND_QUERY_KEYS = [
	'dogname',
	'dogweight',
	'weighttype',
	'dogbreed',
	'uname',
	'uemail',
	'doggender',
	'agetype',
	'dogage',
	'healthtags',
	'from',
	'infoscreen',
];

const healthyDogToolTranslations = (() => {
	const translationsElement = document.getElementById('healthyDogToolTranslations');

	if (!translationsElement) return {};

	try {
		return JSON.parse(translationsElement.textContent);
	} catch (_error) {
		return {};
	}
})();

const decodeHtmlEntities = (value) => {
	if (typeof value !== 'string') return value;

	const textarea = document.createElement('textarea');

	textarea.innerHTML = value;

	return textarea.value;
};

const decodeHtmlEntitiesDeep = (value) => {
	if (typeof value === 'string') {
		return decodeHtmlEntities(value);
	}

	if (Array.isArray(value)) {
		return value.map((item) => decodeHtmlEntitiesDeep(item));
	}

	if (value && typeof value === 'object') {
		return Object.keys(value).reduce((accumulator, key) => {
			accumulator[key] = decodeHtmlEntitiesDeep(value[key]);

			return accumulator;
		}, {});
	}

	return value;
};

const parseJsonScriptContent = (scriptId) => {
	const element = document.getElementById(scriptId);

	if (!element) return {};

	try {
		const parsedJson = JSON.parse(element.textContent);

		return decodeHtmlEntitiesDeep(parsedJson);
	} catch (_error) {
		return {};
	}
};

const hdtT = (key, fallback = '') => {
	const translatedValue = healthyDogToolTranslations?.[key] || fallback;

	return decodeHtmlEntities(translatedValue);
};

const escapeHtml = (string) => {
	const entityMap = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;',
		'/': '&#x2F;',
		'`': '&#x60;',
		'=': '&#x3D;',
	};

	return String(string).replace(/[&<>"'`=\/]/g, (s) => entityMap[s]);
};

const interpolateDogName = (template, dogname) => {
	return String(template || '').replace(/\{\{userDetails\.dogname\}\}/g, escapeHtml(dogname || ''));
};

const isIframePage = () => window.location.href === IFRAME_PAGE;

const slugify = (value) =>
	String(value || '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const postIframeHeight = (delay = 50) => {
	if (!isIframePage()) return;

	setTimeout(() => {
		parent.postMessage(document.body.clientHeight, '*');
	}, delay);
};

const scrollToElement = (element) => {
	if (!element) return;

	const top = element.getBoundingClientRect().top + window.pageYOffset - 20;

	window.scrollTo({ top, behavior: 'smooth' });
};

const getStorageJson = (key, fallback) => {
	try {
		const raw = localStorage.getItem(key);

		if (!raw) return fallback;

		return JSON.parse(raw);
	} catch (_error) {
		return fallback;
	}
};

const setStorageJson = (key, value) => {
	localStorage.setItem(key, JSON.stringify(value));
};

if (typeof HealthyDogTool !== 'function') {
	class HealthyDogTool {
		constructor(root) {
			this.root = root;
			this.quizEl = root.querySelector('#quizApp');
			this.dashboardEl = root.querySelector('#personalDashboard');

			this.quizStructure = parseJsonScriptContent('quizStructure');
			this.detailsTree = parseJsonScriptContent('detailsStructure');
			this.dosages = parseJsonScriptContent('productDosages');
			this.dogBreeds = parseJsonScriptContent('dogBreeds');
			this.dogFood = parseJsonScriptContent('dogFood');

			this.hydrateProductAvailability();

			this.state = {
				currentSection: 0,
				currentSubSection: 0,
				showQuizHeading: true,
				showPrevBack: true,
				path: [],
				pathNext: '',
				savedAnswers: {},
				userDetails: { dogweight_type: 'lb' },
				articleTags: ['general articles'],
				autoCompleted: [],
				autoCompleteShow: false,
				dropdownOpen: false,
				featuredArticlesHTML: '',
				updateDetailInputs: false,
				collapseDetails: false,
				liverCleanseShow: false,
				liverCleanseHide: true,
				addToCart: true,
				sendEmail: true,
				productsParsed: [],
				view: 'quiz',
			};

			this.essentialsLoaded = false;
			this.boundOnDocumentClick = this.onDocumentClick.bind(this);
			// Until the first render is out, ?step= is corrected in place instead of stacking history entries
			this.historyReady = false;

			// Homepage / recipe-maker query params must land in storage before restore, otherwise
			// a deferred vm-global handoff used to write them after the first paint.
			this.applyInboundHandoff();
			this.expireStaleStorage();
			this.restoreFromStorage();
			this.applyCheckedDefaults();
			this.applyBootStep();
			this.bindEvents();
			this.renderQuiz();
			this.syncDashboard();

			if (this.state.view !== 'quiz') this.setView(this.state.view);

			this.syncStepUrl('replace');
			this.historyReady = true;

			// Releases the boot overlay once real markup is in the DOM
			this.root.classList.add('is-ready');
			this.root.setAttribute('aria-busy', 'false');

			this.injectPrintStyles();
			this.loadEssentialProducts();
			postIframeHeight(300);
		}

		// Appended last so it beats app.css (`#wrapper { min-height: 100vh; display: flex }`
		// and `img { page-break-inside: avoid }`), which load after the tool stylesheet.
		injectPrintStyles() {
			if (document.getElementById('hdt-print-css')) return;

			const style = document.createElement('style');

			style.id = 'hdt-print-css';
			style.textContent = `@media print {
html, body, #wrapper, #main-content, #wrapper > [role="main"],
.shopify-section--healthy-dog-tool, .quizPersonalizeDashboard,
.personalizedIntroductionWrapper, #personalDashboard, .product-intro-wrapper {
	display: block !important;
	float: none !important;
	position: static !important;
	transform: none !important;
	min-height: 0 !important;
	height: auto !important;
	max-height: none !important;
	overflow: visible !important;
	flex: none !important;
	padding-right: 0 !important;
}
#wrapper > .shopify-section:has(header),
#wrapper > .shopify-section:has(.trustpilot-top-bar),
#wrapper > .shopify-section:has(.announcement-bar),
#wrapper > .shopify-section:has(.footer-top-bar),
#quizApp, .hdt-boot-screen, .personalizedIntroductionStep, .product-intro-detox,
.disclaimer-pop, .disclaimer-blackout, img.leaf-quiz, img.leaf2-quiz,
.pis-sidebar-details, #personalizedSteps, .pi-top-question, .product-intro-heading,
.pis-pagination, .order-now, #Cart-Drawer, #Product-Drawer, .click-capture,
shopify-preview-bar, #preview-bar-iframe, .shopify-preview-bar {
	display: none !important;
	height: 0 !important;
	min-height: 0 !important;
	overflow: hidden !important;
}
.personalizedIntroductionWrapper__image {
	display: block !important;
	height: auto !important;
	width: auto !important;
	max-height: 54px !important;
	max-width: 180px !important;
	margin: 0 auto 16px !important;
	float: none !important;
}
img {
	page-break-inside: auto !important;
	break-inside: auto !important;
	max-height: 90px;
}
.week-product-dosage img, .pil-product img {
	max-height: 80px;
	width: auto;
}
footer, .footer, .footer__bottom-section {
	display: block !important;
	position: static !important;
	float: none !important;
	min-height: 0 !important;
	margin: 16px 0 0 !important;
	padding: 16px 0 0 !important;
	page-break-before: auto !important;
	break-before: auto !important;
}
}`;

			document.head.appendChild(style);
		}

		hydrateProductAvailability() {
			const products = this.dosages.products || [];
			const reviewsData = window.store && window.store.reviewsData;

			if (!reviewsData) return;

			products.forEach((product) => {
				const data = reviewsData[product.handle];

				if (!data) return;

				product.available = data.available === true || data.available === 'true';
				product.variantId = Number(data.variant_id || data.variantId);
			});
		}

		restoreFromStorage() {
			const storedPath = getStorageJson('pd_user_path', null);

			if (Array.isArray(storedPath) && storedPath.length) {
				this.state.path = storedPath;
				this.state.currentSection = storedPath[storedPath.length - 1][0];
				this.state.currentSubSection = storedPath[storedPath.length - 1][1];
			}

			const storedView = localStorage.getItem('pd_hdt_view');

			if (storedView && VIEW_STEP_SLUGS[storedView]) this.state.view = storedView;

			const storedAnswers = getStorageJson('pd_user_answers', null);

			if (storedAnswers && typeof storedAnswers === 'object') {
				this.state.savedAnswers = storedAnswers;
			}

			const storedDetails = getStorageJson('pd_user_details', null);

			if (storedDetails && typeof storedDetails === 'object') {
				this.state.userDetails = storedDetails;
			}

			const storedTags = getStorageJson('pd_article_tags', null);

			if (Array.isArray(storedTags)) {
				this.state.articleTags = storedTags;
			}

			if (!this.state.userDetails.dogweight_type) {
				this.state.userDetails.dogweight_type = 'lb';
			}
		}

		applyCheckedDefaults() {
			const sections = this.quizStructure.sections || [];

			sections.forEach((section, sectionIndex) => {
				(section.sub_sections || []).forEach((subSection, subIndex) => {
					(subSection.answers || []).forEach((answer, answerIndex) => {
						if (answer.checked !== true) return;

						const key = `${sectionIndex}_${subIndex}`;

						if (!this.state.savedAnswers[key]) {
							this.state.savedAnswers[key] = [answerIndex];
						} else if (!this.state.savedAnswers[key].includes(answerIndex)) {
							this.state.savedAnswers[key].push(answerIndex);
						}
					});
				});
			});
		}

		persistAnswers() {
			setStorageJson('pd_user_answers', this.state.savedAnswers);
			this.touchLastVisit();
		}

		persistDetails() {
			setStorageJson('pd_user_details', this.state.userDetails);
			this.touchLastVisit();
		}

		persistTags() {
			setStorageJson('pd_article_tags', this.state.articleTags);
			this.touchLastVisit();
		}

		persistPath() {
			setStorageJson('pd_user_path', this.state.path);
			this.touchLastVisit();
		}

		persistView() {
			localStorage.setItem('pd_hdt_view', this.state.view);
			this.touchLastVisit();
		}

		touchLastVisit() {
			try {
				if (localStorage.getItem(LAST_VISIT_KEY)) return;

				localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
			} catch (_error) {
				// Storage blocked (private mode)
			}
		}

		restartStorageTtl() {
			try {
				localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
			} catch (_error) {
				// Storage blocked (private mode)
			}
		}

		clearPersistedQuizData() {
			[
				'pd_user_path',
				'pd_user_answers',
				'pd_user_details',
				'pd_article_tags',
				'pd_hdt_view',
				'pd_source',
				PLAN_REACHED_AT_KEY,
				LAST_VISIT_KEY,
			].forEach((key) => {
				try {
					localStorage.removeItem(key);
				} catch (_error) {
					// noop
				}
			});
		}

		// Homepage calculator, recipe maker and email links used to be applied from vm-global.js
		// after this script had already restored stale storage and painted.
		applyInboundHandoff() {
			const params = this.readInboundParams();
			const from = params.from;

			this.maybeResetEmailConfirmationPath(from);

			if (from === 'homepage') {
				this.applyHomepageHandoff(params);
				this.stripInboundParams(params);

				return;
			}

			if (params.infoscreen === 'true') {
				this.writeStorageJson('pd_user_path', [
					[0, 0],
					[2, 0],
				]);
			}

			if (params.dogname && params.dogweight && params.weighttype) {
				this.applyRecipeMakerHandoff(params);
				this.stripInboundParams(params);

				return;
			}

			this.markSource('internal');
		}

		readInboundParams() {
			const fromSearch = (search) => {
				const params = {};

				new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).forEach((value, key) => {
					params[key] = value;
				});

				return params;
			};

			const hasInbound = (params) => INBOUND_QUERY_KEYS.some((key) => params[key] != null && params[key] !== '');
			const params = fromSearch(window.location.search);

			if (hasInbound(params)) return params;

			const raw = window.location.search.slice(1);

			if (!raw) return params;

			try {
				const decodedParams = fromSearch(atob(raw));

				if (hasInbound(decodedParams)) return decodedParams;
			} catch (_error) {
				// Search is not a base64 payload from an email link
			}

			return params;
		}

		maybeResetEmailConfirmationPath(from) {
			const storedPath = getStorageJson('pd_user_path', null);

			if (!Array.isArray(storedPath) || storedPath.length !== 7) return;

			if (from !== 'hdtmail') {
				this.writeStorageJson('pd_user_path', [[0, 0]]);

				return;
			}

			this.stripInboundParams({ from: 'hdtmail' });
		}

		applyHomepageHandoff(params) {
			const healthTags = this.normalizeHealthTags(params.healthtags);
			const details = this.userDetailsFromParams(params, {
				dogfoodtype: [],
				healthTags: healthTags.join(', '),
			});
			const concernMatch = this.matchHealthConcerns(healthTags);

			this.clearPlanProgress();
			this.writeStorageJson('pd_user_details', details);
			this.markSource('internal');
			this.restartStorageTtl();

			if (healthTags.length) {
				this.writeStorageJson('pd_user_path', [
					[0, 0],
					[1, 0],
					[1, 1],
					[1, 3],
				]);
				this.writeStorageJson('pd_user_answers', {
					'0_0': [0],
					'1_0': [1],
					'1_1_0': [...concernMatch.indexes],
					'1_1': [],
				});
				this.writeStorageJson(
					'pd_article_tags',
					concernMatch.tags.length ? concernMatch.tags : ['general articles']
				);

				return;
			}

			this.writeStorageJson('pd_user_path', [
				[0, 0],
				[1, 0],
				[1, 3],
			]);
			this.writeStorageJson('pd_user_answers', { '0_0': [0], '1_0': [0] });
			this.writeStorageJson('pd_article_tags', ['general articles']);
		}

		applyRecipeMakerHandoff(params) {
			const details = this.userDetailsFromParams(params, {
				dogfoodtype: ['Raw Food'],
			});

			window.store = window.store || {};
			window.store.recipeMakerSource = true;
			window.store.recipeMakerHeadings = {
				h1: 'Congratulations on completing your recipe.',
				h2: `Let's create ${details.dogname}'s supplement plan`,
			};

			this.clearPlanProgress();
			this.writeStorageJson('pd_user_path', [[0, 0]]);
			this.writeStorageJson('pd_user_details', details);
			this.markSource('external');
			this.restartStorageTtl();
		}

		userDetailsFromParams(params, extras = {}) {
			const weightType = params.weighttype === 'lbs' ? 'lb' : params.weighttype || 'lb';

			return {
				dogweight_type: weightType,
				dogname: params.dogname || '',
				dogweight: params.dogweight || '',
				username: params.uname || '',
				emailaddress: params.uemail || '',
				dogbreedtype: params.dogbreed || '',
				doggender: params.doggender || '',
				dogage: params.dogage || '',
				dogage_type: params.agetype || '',
				to_subscribe: true,
				to_course: true,
				...extras,
			};
		}

		normalizeHealthTags(raw) {
			if (!raw) return [];

			return String(raw)
				.replace(/%20/g, ' ')
				.split(',')
				.map((tag) => tag.trim())
				.filter(Boolean);
		}

		healthConcernOptions() {
			const values = this.quizStructure?.sections?.[1]?.sub_sections?.[1]?.answers?.[0]?.values;

			if (Array.isArray(values) && values.length) {
				return values.map((pair, index) => ({
					name: pair[0],
					value: pair[1],
					index,
				}));
			}

			return (window.store && window.store.healthConcers) || [];
		}

		matchHealthConcerns(tags) {
			const options = this.healthConcernOptions();
			const indexes = [];
			const matchedTags = [];

			tags.forEach((tag) => {
				const lower = tag.toLowerCase();
				// Old homepage calculator hardcoded the first option as "blog"
				const match =
					lower === 'blog'
						? options[0]
						: options.find(
								(concern) =>
									String(concern.value || '').toLowerCase() === lower ||
									String(concern.name || '').toLowerCase() === lower
						  );

				if (!match) return;

				indexes.push(match.index);
				matchedTags.push(match.value);
			});

			indexes.sort((a, b) => a - b);

			if (matchedTags.length && !matchedTags.includes('general articles')) {
				matchedTags.push('general articles');
			}

			return { indexes, tags: matchedTags };
		}

		writeStorageJson(key, value) {
			try {
				setStorageJson(key, value);
			} catch (_error) {
				// Storage blocked (private mode)
			}
		}

		clearPlanProgress() {
			try {
				localStorage.removeItem('pd_hdt_view');
				localStorage.removeItem(PLAN_REACHED_AT_KEY);
			} catch (_error) {
				// noop
			}
		}

		markSource(source) {
			try {
				localStorage.setItem('pd_source', source);
			} catch (_error) {
				// noop
			}

			if (!this.quizEl) return;

			this.quizEl.classList.toggle('is-external', source === 'external');
			this.quizEl.classList.toggle('is-internal', source !== 'external');
		}

		stripInboundParams(params) {
			if (!this.canWriteHistory()) return;

			const inboundKeys = new Set(INBOUND_QUERY_KEYS);
			const url = new URL(window.location.href);
			const hasPlainInbound = INBOUND_QUERY_KEYS.some((key) => url.searchParams.has(key));

			if (!hasPlainInbound && params && (params.dogname || params.from)) {
				const kept = new URLSearchParams();

				Object.keys(params).forEach((key) => {
					if (!inboundKeys.has(key) && params[key] != null && params[key] !== '') {
						kept.set(key, params[key]);
					}
				});

				history.replaceState({}, '', `${window.location.pathname}${kept.toString() ? `?${kept}` : ''}${window.location.hash}`);

				return;
			}

			let changed = false;

			inboundKeys.forEach((key) => {
				if (!url.searchParams.has(key)) return;

				url.searchParams.delete(key);
				changed = true;
			});

			if (changed) history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
		}

		expireStaleStorage() {
			let lastVisit = null;

			try {
				const raw = Number(localStorage.getItem(LAST_VISIT_KEY));

				lastVisit = Number.isFinite(raw) && raw > 0 ? raw : null;
			} catch (_error) {
				return;
			}

			if (lastVisit === null) return;

			if (Date.now() - lastVisit < STORAGE_TTL_MS) return;

			this.clearPersistedQuizData();
		}

		canonicalizeStepSlug(slug) {
			if (!slug) return '';

			return LEGACY_STEP_SLUGS[slug] || slug;
		}

		isKnownStepSlug(slug) {
			const canonical = this.canonicalizeStepSlug(slug);

			if (!canonical) return false;

			return (
				Object.values(VIEW_STEP_SLUGS).includes(canonical) || Object.values(QUIZ_STEP_SLUGS).includes(canonical)
			);
		}

		// Storage first, then a known ?step= overlay so a refresh on quiz-end stays there.
		// utm / campaign params with no step of their own never match, so they cannot hijack.
		applyBootStep() {
			if (this.restartIfPlanExpired()) return;

			const urlSlug = this.canonicalizeStepSlug(new URLSearchParams(window.location.search).get(STEP_PARAM) || '');

			if (this.isKnownStepSlug(urlSlug)) this.applyStepSlug(urlSlug);

			if (this.stepSlug() === RESTART_ON_LOAD_SLUG) this.restartToFirstStep();
		}

		applyStepSlug(slug) {
			const canonical = this.canonicalizeStepSlug(slug);
			const view = Object.keys(VIEW_STEP_SLUGS).find((key) => VIEW_STEP_SLUGS[key] === canonical);

			if (view) {
				this.state.view = view;
				return;
			}

			const key = Object.keys(QUIZ_STEP_SLUGS).find((entry) => QUIZ_STEP_SLUGS[entry] === canonical);

			if (!key) return;

			const [section, subSection] = key.split('_').map(Number);

			this.state.view = 'quiz';
			this.state.currentSection = section;
			this.state.currentSubSection = subSection;
			this.trimPath(section, subSection);
		}

		readPlanReachedAt() {
			try {
				const raw = Number(localStorage.getItem(PLAN_REACHED_AT_KEY));

				return Number.isFinite(raw) && raw > 0 ? raw : null;
			} catch (_error) {
				return null;
			}
		}

		// Stamped when the customer actually reaches the weekly plan, never when a stored view is
		// replayed on boot - otherwise every page load would push the 48h window forward.
		markPlanReached() {
			try {
				localStorage.setItem(PLAN_REACHED_AT_KEY, String(Date.now()));
			} catch (_error) {
				// Storage blocked (private mode): the plan simply never expires for this visitor
			}
		}

		clearPlanReached() {
			try {
				localStorage.removeItem(PLAN_REACHED_AT_KEY);
			} catch (_error) {
				// noop
			}
		}

		// True when stale progress was cleared and the customer should start the tool over
		restartIfPlanExpired() {
			const reachedAt = this.readPlanReachedAt();

			if (reachedAt === null) {
				// Storage written before this expiry existed - treat this visit as the arrival so
				// returning customers still get sent back around 48h from now
				if (this.state.view === 'dosages' || this.state.view === 'ordered') this.markPlanReached();

				return false;
			}

			if (Date.now() - reachedAt < PLAN_TTL_MS) return false;

			this.clearPlanReached();
			this.restartToFirstStep();

			return true;
		}

		restartToFirstStep() {
			this.state.view = 'quiz';
			this.state.currentSection = 0;
			this.state.currentSubSection = 0;
			this.state.path = [[0, 0]];
			this.persistPath();
			this.persistView();
		}

		currentSub() {
			return this.quizStructure.sections?.[this.state.currentSection]?.sub_sections?.[this.state.currentSubSection] || {};
		}

		savePath() {
			const reset = localStorage.getItem('reset');

			if (reset) {
				this.state.path = [[0, 0]];
				localStorage.removeItem('reset');
			}

			const alreadySaved = this.state.path.some(
				(entry) => entry[0] === this.state.currentSection && entry[1] === this.state.currentSubSection
			);

			if (!alreadySaved) {
				this.state.path.push([this.state.currentSection, this.state.currentSubSection]);
			}

			this.persistPath();
		}

		setDefaultPath() {
			this.state.pathNext = this.currentSub().default_path || '';
		}

		stepSlug() {
			if (this.state.view !== 'quiz') return VIEW_STEP_SLUGS[this.state.view] || '';

			const key = `${this.state.currentSection}_${this.state.currentSubSection}`;

			if (Object.prototype.hasOwnProperty.call(QUIZ_STEP_SLUGS, key)) return QUIZ_STEP_SLUGS[key];

			// Questions added to the JSON later still get a readable value instead of dropping out of tracking
			return slugify(this.currentSub().path_name) || `step-${this.state.currentSection}-${this.state.currentSubSection}`;
		}

		// The tool also runs inside the iframe page and in the theme editor, where rewriting the URL is pointless
		canWriteHistory() {
			return (
				!isIframePage() &&
				window.self === window.top &&
				!window.Shopify?.designMode &&
				typeof window.history?.pushState === 'function'
			);
		}

		// Writes ?step=<slug> and fires the GA event. Incoming params (utm, email campaign
		// ids, leftover keys from other sections) are copied as-is - the tool only touches
		// its own key, and only after state is known, so a link with no step of its own
		// still gets one appended rather than being misread as a deep link.
		syncStepUrl(mode) {
			const slug = this.stepSlug();

			if (slug === this.trackedStepSlug) return;

			this.trackedStepSlug = slug;
			this.trackStep(slug);

			if (!this.canWriteHistory()) return;

			const params = new URLSearchParams(window.location.search);

			if (slug) params.set(STEP_PARAM, slug);
			else params.delete(STEP_PARAM);

			const query = params.toString();
			const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
			const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

			if (url === current) return;

			// The first render corrects whatever arrived in the URL, later steps get their own entry
			if (mode === 'replace') window.history.replaceState({ hdtStep: slug }, '', url);
			else window.history.pushState({ hdtStep: slug }, '', url);
		}

		trackStep(slug) {
			const step = slug || 'start';
			const payload = { healthy_dog_tool_step: step };

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({ event: 'healthy_dog_tool_step', ...payload });

			// Same as recipe maker: only send to GA when gtag exists. This tool boots
			// before GTM, so the first step used to fire into dataLayer only and GA
			// recorded the event name with no slug — empty funnel, one Explore row.
			const sendToGa = () => {
				if (typeof window.gtag !== 'function') return false;

				window.gtag('event', 'healthy_dog_tool_step', payload);

				return true;
			};

			if (sendToGa()) return;

			let attempts = 0;
			const timer = window.setInterval(() => {
				attempts += 1;

				if (sendToGa() || attempts > 40) window.clearInterval(timer);
			}, 250);
		}

		onPopState() {
			const slug = new URLSearchParams(window.location.search).get(STEP_PARAM) || '';

			this.goToStep(slug);
		}

		// Back/forward navigation. trackedStepSlug is set up front so the render that follows
		// does not push the entry we just came from back onto the stack.
		goToStep(slug) {
			const canonical = this.canonicalizeStepSlug(slug);

			if (!canonical) return;

			if (canonical === RESTART_ON_LOAD_SLUG) {
				this.restartToFirstStep();
				this.trackedStepSlug = '';
				this.setView('quiz');
				this.renderQuiz();
				scrollToElement(this.quizEl);
				this.syncStepUrl('replace');

				return;
			}

			if (canonical === this.stepSlug()) return;

			const view = Object.keys(VIEW_STEP_SLUGS).find((key) => VIEW_STEP_SLUGS[key] === canonical);

			if (view) {
				this.trackedStepSlug = canonical;
				this.setView(view);

				return;
			}

			const key = Object.keys(QUIZ_STEP_SLUGS).find((entry) => QUIZ_STEP_SLUGS[entry] === canonical);

			// An unknown value means the param belongs to something else - leave the tool alone
			if (!key) return;

			const [section, subSection] = key.split('_').map(Number);

			this.trackedStepSlug = canonical;
			this.state.currentSection = section;
			this.state.currentSubSection = subSection;
			this.trimPath(section, subSection);

			if (this.state.view !== 'quiz') this.setView('quiz');

			this.renderQuiz();
			scrollToElement(this.quizEl);
		}

		// Keeps the stored breadcrumb honest when the user navigates with the browser buttons
		trimPath(section, subSection) {
			const index = this.state.path.findIndex((entry) => entry[0] === section && entry[1] === subSection);

			if (index === -1) return;

			this.state.path.length = index + 1;
			this.persistPath();
		}

		setView(view) {
			this.state.view = view;
			this.persistView();
			this.root.setAttribute('data-hdt-view', view);

			const stepMap = {
				quiz: 'quiz',
				essentials: 'essentials',
				dosages: 'dosages',
				ordered: 'ordered',
			};
			const goTo = stepMap[view];

			this.dashboardEl.querySelectorAll('#personalizedSteps li').forEach((item) => {
				item.classList.toggle('active', item.getAttribute('data-go-to') === goTo);

				if (item.getAttribute('data-go-to') === 'ordered') {
					item.style.display = view === 'ordered' ? '' : 'none';
				}
			});

			if (view === 'dosages') {
				this.renderDosages();

				// historyReady is false while the constructor replays a stored view, so only a real
				// navigation to the weekly plan restarts the 48h window
				if (this.historyReady) this.markPlanReached();
			}

			if (view !== 'quiz') {
				scrollToElement(this.dashboardEl);
			}

			this.syncStepUrl(this.historyReady ? 'push' : 'replace');
			postIframeHeight(50);
		}

		bindEvents() {
			this.root.addEventListener('click', (event) => this.onClick(event));
			this.root.addEventListener('input', (event) => this.onInput(event));
			this.root.addEventListener('change', (event) => this.onChange(event));
			this.root.addEventListener('keyup', (event) => this.onKeyup(event));
			this.root.addEventListener('keydown', (event) => this.onKeydown(event));
			document.addEventListener('click', this.boundOnDocumentClick);
			window.addEventListener('popstate', () => this.onPopState());
			window.addEventListener('resize', () => this.syncDosageTracks());
		}

		onDocumentClick(event) {
			if (!event.target.closest('.pill-select')) {
				this.closeDropdowns();
			}

			const autocomplete = this.root.querySelector('.autocomplete-wrapper');
			const autocompleteInput = event.target.closest('[data-hdt-autocomplete]');

			if (autocomplete && !autocomplete.contains(event.target) && !autocompleteInput) {
				this.state.autoCompleteShow = false;
				autocomplete.classList.remove('autoCompleteShow');
			}
		}

		onClick(event) {
			const actionEl = event.target.closest('[data-hdt-action]');

			if (!actionEl) {
				if (event.target.closest('.product-card-quickview')) {
					this.openQuickView(event);
				}

				return;
			}

			const action = actionEl.dataset.hdtAction;

			switch (action) {
				case 'scroll-next': {
					const scroller = actionEl.parentElement?.querySelector('.hdt-dosage-track');

					if (scroller) this.applyDosageOffset(scroller, this.dosageOffset(scroller) + 280);
					break;
				}
				case 'disclaimer-open':
					this.toggleDisclaimer(true);
					break;
				case 'disclaimer-close':
					this.toggleDisclaimer(false);
					break;
				case 'quiz-next':
					this.pathForward();
					if (actionEl.classList.contains('finishquiz')) this.onFinishQuiz();
					break;
				case 'quiz-back':
					this.pathBack();
					break;
				case 'quiz-reset':
					this.resetQuiz();
					this.setView('quiz');
					break;
				case 'save-answer':
					event.preventDefault();
					this.saveAnswerFromEl(actionEl, event);
					break;
				case 'dropdown-toggle':
					this.toggleDropdown(actionEl);
					break;
				case 'pill-select':
					this.selectPill(actionEl);
					break;
				case 'autocomplete-pick':
					this.pickAutocomplete(actionEl);
					break;
				case 'intro-show':
					this.showDashboardIntro();
					break;
				case 'intro-next':
					this.setView('dosages');
					break;
				case 'intro-back':
					this.setView('quiz');
					break;
				case 'order-next':
					this.handleOrderNext();
					break;
				case 'dosages-back':
					this.setView('essentials');
					break;
				case 'order-back':
					this.setView('dosages');
					break;
				case 'step-nav':
					this.handleStepNav(actionEl);
					break;
				case 'liver-yes':
					this.setLiverCleanse(true);
					break;
				case 'liver-no':
					this.setLiverCleanse(false);
					break;
				case 'toggle-email':
					this.state.sendEmail = !this.state.sendEmail;
					this.syncDashboardToggles();
					break;
				case 'toggle-cart':
					this.state.addToCart = !this.state.addToCart;
					this.syncDashboardToggles();
					break;
				case 'toggle-sidebar-mobile':
					this.toggleMobileSidebar(actionEl);
					break;
				case 'toggle-product-detail':
					this.toggleProductDetail(actionEl, true);
					break;
				case 'close-product-detail':
					this.toggleProductDetail(actionEl, false);
					break;
				case 'switch-essential-product':
					this.switchEssentialProduct(actionEl);
					break;
				case 'edit-details':
					this.state.updateDetailInputs = !this.state.updateDetailInputs;
					this.syncDashboard();
					break;
				case 'collapse-details':
					this.state.collapseDetails = !this.state.collapseDetails;
					this.syncDashboard();
					break;
				case 'update-food':
					this.updateMultiSelect('dogfoodtype', actionEl.dataset.value);
					break;
				case 'update-weight-type':
					this.saveUserDetails('dogweight_type', actionEl.dataset.value);
					this.syncDashboard();
					this.renderDosages();
					break;
				case 'update-gender':
					this.saveUserDetails('doggender', actionEl.dataset.value);
					this.syncDashboard();
					break;
				case 'update-neutered':
					this.saveUserDetails('dognuetered', actionEl.dataset.value);
					this.syncDashboard();
					break;
				case 'save-radio-detail':
					this.saveUserDetails(actionEl.dataset.key, actionEl.dataset.value);
					this.renderQuiz();
					break;
				case 'toggle-quiz-modal':
					this.quizEl.querySelector('.loading-modal-quiz')?.classList.toggle('is-hidden');
					break;
				default:
					break;
			}
		}

		onInput(event) {
			const field = event.target.closest('[data-hdt-field]');

			if (!field) return;

			this.saveUserDetails(field.dataset.hdtField, field.value);

			if (field.dataset.newsletterField) {
				const newsletterInput = document.querySelector(field.dataset.newsletterField);

				if (newsletterInput) newsletterInput.value = field.value;
			}

			if (this.state.view !== 'quiz') {
				this.syncDashboardBindings();
				if (field.dataset.hdtField === 'dogweight') this.renderDosages();
			}
		}

		onChange(event) {
			const field = event.target.closest('[data-hdt-field]');

			if (!field) return;

			if (field.tagName === 'SELECT' && !field.multiple) {
				this.saveUserDetails(field.dataset.hdtField, field.value);
				this.syncDashboard();
			}
		}

		onKeydown(event) {
			if (event.key === 'Escape') {
				this.closeDropdowns();

				return;
			}

			const trigger = event.target.closest('[data-hdt-action="dropdown-toggle"]');

			if (!trigger || (event.key !== 'Enter' && event.key !== ' ')) return;

			event.preventDefault();
			this.toggleDropdown(trigger);
		}

		onKeyup(event) {
			const autocompleteInput = event.target.closest('[data-hdt-autocomplete]');

			if (!autocompleteInput) return;

			const list = JSON.parse(autocompleteInput.dataset.autocompleteList || '[]');
			const answerIndex = Number(autocompleteInput.dataset.answer);
			const sectionIndex = Number(autocompleteInput.dataset.section);
			const subIndex = Number(autocompleteInput.dataset.sub);

			this.autoComplete(event.target.value, list);

			const wrapper = this.quizEl.querySelector('.autocomplete-wrapper');

			if (!wrapper) return;

			wrapper.classList.toggle('autoCompleteShow', this.state.autoCompleteShow);
			wrapper.innerHTML = this.state.autoCompleted
				.map(
					(item) =>
						`<span data-hdt-action="autocomplete-pick" data-value="${escapeHtml(item)}" data-section="${sectionIndex}" data-sub="${subIndex}" data-answer="${answerIndex}">${escapeHtml(item)}</span>`
				)
				.join('');
		}

		toggleDisclaimer(show) {
			this.root.querySelector('.disclaimer-pop')?.classList.toggle('is-visible', show);
			this.root.querySelector('.disclaimer-blackout')?.classList.toggle('is-visible', show);
		}

		tagShow(tagged, tags) {
			if (tagged === undefined) return true;

			if (!tags || !tags.length) return false;

			return tags.some((tag) => this.state.articleTags.includes(tag));
		}

		answerChecker(sectionIndex, subIndex, answerIndex, _checked, type) {
			const key = `${sectionIndex}_${subIndex}`;

			if (type === 'radio') {
				if (this.state.savedAnswers[key] == answerIndex || (Array.isArray(this.state.savedAnswers[key]) && this.state.savedAnswers[key][0] == answerIndex)) {
					this.state.pathNext = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex].path_to;

					return 'checked';
				}

				return '';
			}

			if (type === 'checkbox') {
				if (this.state.savedAnswers[key] && this.state.savedAnswers[key].includes(answerIndex)) {
					this.state.pathNext = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex].path_to;

					return 'checked';
				}

				return '';
			}

			return '';
		}

		pillChecker(sectionIndex, subIndex, answerIndex, pillIndex) {
			const key = `${sectionIndex}_${subIndex}_${answerIndex}`;

			if (this.state.savedAnswers[key] && this.state.savedAnswers[key].includes(pillIndex)) {
				this.state.pathNext = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex].path_to;

				return 'checked';
			}

			return '';
		}

		loadSavedDetails(key, type, val) {
			if (!this.state.userDetails.hasOwnProperty(key)) return type ? '' : '';

			if (type === 'select' || type === 'radio') {
				if (val === this.state.userDetails[key]) return type === 'radio' ? 'checked' : 'selected';

				return '';
			}

			if (type === 'multiselect') {
				if (this.state.userDetails[key] && this.state.userDetails[key].indexOf(val) !== -1) return 'checked';

				return '';
			}

			return this.state.userDetails[key];
		}

		saveUserDetails(key, value, multiple) {
			if (multiple === true) {
				const select = this.root.querySelector(`#${key}`);

				this.state.userDetails[key] = select ? Array.from(select.selectedOptions).map((option) => option.value) : value;
			} else {
				this.state.userDetails[key] = value;
			}

			this.persistDetails();
		}

		updateMultiSelect(id, value) {
			const selected = Array.isArray(this.state.userDetails[id]) ? [...this.state.userDetails[id]] : [];
			const index = selected.indexOf(value);

			if (index === -1) {
				selected.push(value);
			} else {
				selected.splice(index, 1);
			}

			this.state.userDetails[id] = selected;
			this.persistDetails();
			this.syncFoodSelection(selected);
			this.syncDashboard();
		}

		hideArticles(hide) {
			if (hide !== true) return;

			this.state.articleTags = ['general articles'];
			this.persistTags();
		}

		newsletterTrigger(input, target) {
			if (input) {
				document.querySelector(input)?.click();
			}

			if (input !== '#community_newsletter' && input !== '#health_longevity_newsletter') return;

			const parentElement = target.closest('.single-question');
			const isChecked = parentElement && !parentElement.classList.contains('checked');
			const currentDetails = getStorageJson('pd_user_details', {}) || {};

			if (input === '#community_newsletter') {
				currentDetails.to_subscribe = isChecked;
			}

			if (input === '#health_longevity_newsletter') {
				currentDetails.to_course = isChecked;
			}

			this.state.userDetails = { ...this.state.userDetails, ...currentDetails };
			this.persistDetails();
		}

		clearOthers(clear, _subSectionId, question) {
			if (clear !== true) return;

			const currentQuestion = `s${question}`;

			if (currentQuestion === 'sq110') {
				this.state.savedAnswers['1_1'] = [];
			} else {
				this.state.savedAnswers['1_1_0'] = [];
			}

			if (currentQuestion === 'sq112') {
				this.state.savedAnswers['1_1'] = [];
				this.state.savedAnswers['1_1_0'] = [];
			}

			if (currentQuestion === 'sq111' || currentQuestion === 'sq110') {
				const textInput = this.quizEl.querySelector('#text_112');

				if (textInput) textInput.value = '';
			}

			this.persistAnswers();
		}

		updateArticleTags(assoc, value) {
			if (!assoc) return;

			if (this.state.articleTags.includes(value)) {
				this.state.articleTags.splice(this.state.articleTags.indexOf(value), 1);
			} else {
				this.state.articleTags.push(value);
			}

			if (!this.state.articleTags.includes('general articles')) {
				this.state.articleTags.push('general articles');
			}

			this.persistTags();
		}

		loadArticleTags(assoc, value) {
			if (!assoc) return;

			if (!this.state.articleTags.includes(value)) {
				this.state.articleTags.push(value);
			}

			this.persistTags();
		}

		saveAnswerFromEl(actionEl, event) {
			const sectionIndex = Number(actionEl.dataset.section);
			const subIndex = Number(actionEl.dataset.sub);
			const answerIndex = Number(actionEl.dataset.answer);
			const type = actionEl.dataset.type;

			this.saveAnswer(sectionIndex, subIndex, answerIndex, type, event);
		}

		saveAnswer(sectionIndex, subIndex, answerIndex, type, event) {
			const answer = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex];

			if (String(event?.target?.value || '').indexOf('http') !== -1) return;

			this.state.pathNext = answer.path_to;

			if (type === 'radio' || type === 'text' || type === 'select') {
				this.state.savedAnswers[`${sectionIndex}_${subIndex}`] = [answerIndex];

				if (type === 'radio') {
					this.newsletterTrigger(answer.newsletter_input, event.target);
					this.hideArticles(answer.hide_articles);
					this.clearOthers(answer.clear_others, `qsubsec_${sectionIndex}_${subIndex}`, `q${sectionIndex}${subIndex}${answerIndex}`);
					this.persistAnswers();
					this.pathForward();

					return;
				}
			}

			if (type === 'checkbox') {
				const key = `${sectionIndex}_${subIndex}`;

				if (!this.state.savedAnswers[key]) {
					this.state.savedAnswers[key] = [answerIndex];
				} else if (this.state.savedAnswers[key].includes(answerIndex)) {
					this.state.savedAnswers[key].splice(this.state.savedAnswers[key].indexOf(answerIndex), 1);
				} else {
					this.state.savedAnswers[key].push(answerIndex);
				}

				this.newsletterTrigger(answer.newsletter_input, event.target);
				this.hideArticles(answer.hide_articles);
				this.clearOthers(answer.clear_others, `qsubsec_${sectionIndex}_${subIndex}`, `q${sectionIndex}${subIndex}${answerIndex}`);
			}

			this.persistAnswers();
			this.refreshAnswerStates();
		}

		// Repaints the checked state of the current step in place. A full renderQuiz() would replay
		// the fade animation and, on steps that append article-fetcher, refetch the article list.
		refreshAnswerStates() {
			const sectionIndex = this.state.currentSection;
			const subIndex = this.state.currentSubSection;

			(this.currentSub().answers || []).forEach((answer, answerIndex) => {
				const box = this.quizEl.querySelector(`#sq${sectionIndex}${subIndex}${answerIndex}`);

				if (!box) return;

				if (answer.answer_type === 'checkbox' || answer.answer_type === 'radio') {
					const checked = this.answerChecker(sectionIndex, subIndex, answerIndex, answer.checked, answer.answer_type);

					box.classList.toggle('checked', checked === 'checked');
				}

				if (answer.answer_type === 'pill-selector') {
					const selected = this.state.savedAnswers[`${sectionIndex}_${subIndex}_${answerIndex}`] || [];

					this.syncDropdown(box.querySelector('.pill-select'), (item) => selected.includes(Number(item.dataset.pill)));
				}
			});

			postIframeHeight(50);
		}

		// Repaints a dropdown in place: every item state plus the truncated trigger label
		syncDropdown(wrap, isChecked) {
			if (!wrap) return;

			const labels = [];

			wrap.querySelectorAll('.single-pill').forEach((item) => {
				const checked = isChecked(item);

				if (checked) labels.push(item.textContent.trim());

				item.classList.toggle('checked', checked);
				item.setAttribute('aria-selected', checked ? 'true' : 'false');
			});

			wrap.classList.toggle('has-selection', labels.length > 0);

			const placeholder = wrap.dataset.hdtPlaceholder || '';

			wrap.querySelectorAll('[data-hdt-pill-label]').forEach((label) => {
				label.textContent = this.dropdownSummary(labels, Number(label.dataset.hdtPillLabel), placeholder);
			});
		}

		// "Allergies, Bad Breath… (3 more)" - limit is 2 on desktop, 1 on mobile
		dropdownSummary(labels, limit, placeholder) {
			if (!labels.length) return placeholder;

			const shown = labels.slice(0, limit).join(', ');
			const remaining = labels.length - limit;

			if (remaining <= 0) return shown;

			return `${shown}… (${remaining} ${hdtT('quiz_more', 'more')})`;
		}

		toggleDropdown(actionEl) {
			const wrap = actionEl.closest('.pill-select');

			if (!wrap) return;

			const isOpen = !wrap.classList.contains('is-open');

			this.closeDropdowns();
			this.state.dropdownOpen = isOpen;
			this.applyDropdownState(wrap, isOpen);
		}

		closeDropdowns() {
			if (!this.state.dropdownOpen) return;

			this.state.dropdownOpen = false;
			this.root.querySelectorAll('.pill-select.is-open').forEach((wrap) => this.applyDropdownState(wrap, false));
		}

		applyDropdownState(wrap, isOpen) {
			wrap.classList.toggle('is-open', isOpen);
			wrap.querySelector('.pill-expander')?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
		}

		selectPill(actionEl) {
			const sectionIndex = Number(actionEl.dataset.section);
			const subIndex = Number(actionEl.dataset.sub);
			const answerIndex = Number(actionEl.dataset.answer);
			const pillIndex = Number(actionEl.dataset.pill);
			const answer = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex];
			const key = `${sectionIndex}_${subIndex}_${answerIndex}`;
			const pillValue = answer.values[pillIndex][1];

			if (!this.state.savedAnswers[key]) {
				this.state.savedAnswers[key] = [pillIndex];
			} else if (this.state.savedAnswers[key].includes(pillIndex)) {
				this.state.savedAnswers[key].splice(this.state.savedAnswers[key].indexOf(pillIndex), 1);
			} else {
				this.state.savedAnswers[key].push(pillIndex);
			}

			this.state.pathNext = answer.path_to;
			this.updateArticleTags(answer.articleAssociation, pillValue);
			this.clearOthers(answer.clear_others, `qsubsec_${sectionIndex}_${subIndex}`, `q${sectionIndex}${subIndex}${answerIndex}`);
			this.persistAnswers();
			this.refreshAnswerStates();
		}

		autoComplete(value, autocomplete) {
			this.state.autoCompleted = [];

			(autocomplete || []).forEach((item) => {
				if (item.toLowerCase().indexOf(String(value).toLowerCase()) !== -1) {
					this.state.autoCompleted.push(item);
				}
			});

			this.state.autoCompleteShow = value !== '' && value !== undefined;
		}

		pickAutocomplete(actionEl) {
			const value = actionEl.dataset.value;
			const sectionIndex = Number(actionEl.dataset.section);
			const subIndex = Number(actionEl.dataset.sub);
			const answerIndex = Number(actionEl.dataset.answer);
			const answer = this.quizStructure.sections[sectionIndex].sub_sections[subIndex].answers[answerIndex];

			this.state.savedAnswers[`${sectionIndex}_${subIndex}`] = [answerIndex];
			this.state.pathNext = answer.path_to;
			this.state.autoCompleteShow = false;

			if (answer.articleAssociation) {
				this.state.articleTags = [value];
			}

			if (!this.state.articleTags.includes('general articles')) {
				this.state.articleTags.push('general articles');
			}

			this.persistTags();
			this.persistAnswers();
			this.clearOthers(answer.clear_others, `qsubsec_${sectionIndex}_${subIndex}`, `q${sectionIndex}${subIndex}${answerIndex}`);
			this.refreshAnswerStates();

			const input = this.quizEl.querySelector(`#text_${sectionIndex}${subIndex}${answerIndex}`);

			if (input) input.value = value;

			this.quizEl.querySelector('.autocomplete-wrapper')?.classList.remove('autoCompleteShow');
		}

		resolvePath(pathNext) {
			const paths = String(pathNext || '').split('_');

			if (paths.length === 2) {
				for (let i = 0; i < this.quizStructure.sections.length; i += 1) {
					if (this.quizStructure.sections[i].path_name == paths[0]) {
						this.state.currentSection = i;

						for (let j = 0; j < this.quizStructure.sections[i].sub_sections.length; j += 1) {
							if (this.quizStructure.sections[i].sub_sections[j].path_name == paths[1]) {
								this.state.currentSubSection = j;
								break;
							}
						}

						break;
					}
				}

				return;
			}

			if (paths[0] === '') {
				if (this.quizStructure.sections[this.state.currentSection].sub_sections[this.state.currentSubSection + 1]) {
					this.state.currentSubSection += 1;
				} else {
					this.state.currentSection += 1;
					this.state.currentSubSection = 0;
				}

				return;
			}

			if (paths.length === 1) {
				for (let i = 0; i < this.quizStructure.sections.length; i += 1) {
					if (this.quizStructure.sections[i].path_name == paths[0]) {
						this.state.currentSection = i;
						this.state.currentSubSection = 0;
						break;
					}
				}
			}
		}

		validateRequired() {
			let moveForward = true;
			const scope = this.quizEl.querySelector(`#qsubsec_${this.state.currentSection}_${this.state.currentSubSection}`);

			scope?.querySelectorAll('[required]').forEach((input) => {
				if (!input.value) {
					moveForward = false;
					input.classList.add('required-error');
				} else {
					input.classList.remove('required-error');
				}
			});

			return moveForward;
		}

		pathForward() {
			this.quizEl.querySelectorAll('.required-error').forEach((el) => el.classList.remove('required-error'));

			if (!this.validateRequired()) return;

			this.resolvePath(this.state.pathNext);
			this.renderQuiz();
			scrollToElement(this.quizEl);
			postIframeHeight(50);
		}

		pathBack() {
			if (this.state.path.length) {
				this.state.path.splice(this.state.path.length - 1, 1);
			}

			const previous = this.state.path[this.state.path.length - 1] || [0, 0];

			this.state.currentSection = previous[0];
			this.state.currentSubSection = previous[1];
			this.persistPath();
			this.renderQuiz();
			scrollToElement(this.quizEl);
			postIframeHeight(50);
		}

		resetQuiz() {
			localStorage.setItem('reset', 'true');
			this.clearPersistedQuizData();
			this.touchLastVisit();

			this.state.path = [];
			this.state.currentSection = 0;
			this.state.currentSubSection = 0;
			this.state.showQuizHeading = true;
			this.state.showPrevBack = true;
			this.state.savedAnswers = {};
			this.state.articleTags = ['general articles'];
			this.state.userDetails = { dogweight_type: 'lb' };
			this.state.featuredArticlesHTML = '';
			this.applyCheckedDefaults();
			this.renderQuiz();
			postIframeHeight(50);
		}

		checkmarkHtml() {
			return `<div class="quiz-checkmark"><img src="${CHECKMARK_SRC}" alt=""></div>`;
		}

		renderQuiz() {
			this.savePath();
			this.setDefaultPath();

			const section = this.quizStructure.sections[this.state.currentSection];
			const subSection = this.currentSub();
			const hideHeadings = subSection.hideQuizHeadings === true;

			this.state.showQuizHeading = !hideHeadings;
			this.state.showPrevBack = !hideHeadings && subSection.component !== 'quiz-end';

			if (subSection.component === 'quiz-end') {
				this.onQuizEndCreated();
			}

			const heading = this.quizEl.querySelector('[data-hdt-quiz-heading]');
			const headingsSection = this.quizEl.querySelector('[data-hdt-section-headings]');

			if (heading) {
				heading.hidden = !this.state.showQuizHeading;

				const title = this.quizStructure.quiz_title?.[this.state.currentSection] || '';
				const subTitle = this.quizStructure.quiz_sub_title?.[this.state.currentSection] || '';
				const description = this.quizStructure.quiz_title_description?.[this.state.currentSection] || '';
				const recipeHeadings = window.store && window.store.recipeMakerHeadings;

				if (recipeHeadings && this.state.currentSection === 0) {
					heading.querySelector('h1').textContent = recipeHeadings.h1;
					heading.querySelector('h2').textContent = recipeHeadings.h2;
					heading.querySelector('h3').hidden = true;
				} else {
					heading.querySelector('h1').textContent = title;
					heading.querySelector('h2').textContent = subTitle;
					heading.querySelector('h3').textContent = description;
					heading.querySelector('h3').hidden = !description;
				}
			}

			if (headingsSection) {
				headingsSection.hidden = !this.state.showQuizHeading;
				headingsSection.querySelector('ul').innerHTML = (this.quizStructure.sections || [])
					.map(
						(item, index) => `
						<li class="${this.state.currentSection === index ? 'active' : ''}">
							<span class="sectionIcon" style="background-image:url(${item.icon_image})"></span>
							<strong>${item.title}</strong>
						</li>`
					)
					.join('');
			}

			const wrapper = this.quizEl.querySelector('[data-hdt-quiz-wrapper]');

			if (!wrapper) return;

			const stepKey = `${this.state.currentSection}_${this.state.currentSubSection}`;
			// Fade in only when the visitor actually moves to another step, so an in-step
			// re-render never replays the animation on content that is already on screen
			const stepChanged = this.renderedStepKey !== stepKey;

			this.renderedStepKey = stepKey;

			if (stepChanged) this.state.dropdownOpen = false;

			wrapper.innerHTML = `
				<section class="quizSectionWrapper">
					<section class="${stepChanged ? 'fadein' : ''}" id="qsubsec_${stepKey}">
						<div class="question-heading">
							${this.renderQuestionHeading(subSection)}
							${subSection.answers_type === 'component' ? this.renderComponent(subSection.component) : this.renderAnswers(subSection)}
							${subSection.appendComponent ? this.renderComponent(subSection.appendComponent) : ''}
						</div>
						${this.renderQuizNav()}
					</section>
				</section>
			`;

			if (subSection.show_article_heading) {
				this.loadFeaturedArticles(subSection.show_article_heading);
			}

			if (subSection.appendComponent === 'article-fetcher' || subSection.component === 'article-fetcher') {
				this.fetchArticles();
			}

			if (stepChanged) this.syncStepUrl(this.historyReady ? 'push' : 'replace');

			this.syncDashboardBindings();
		}

		// Shared multi-select dropdown markup for the health concerns and dog food type pickers.
		// The placeholder rides on the wrapper so syncDropdown() can rebuild the label without
		// knowing which of the two it is repainting.
		renderDropdown({ modifier, placeholder, itemAction, options }) {
			const labels = options.filter((option) => option.checked).map((option) => option.label);
			const summaryLabel = (limit) =>
				`<span class="pill-expander__label${
					limit === 1 ? ' pill-expander__label--mobile' : ''
				}" data-hdt-pill-label="${limit}">${escapeHtml(this.dropdownSummary(labels, limit, placeholder))}</span>`;

			return `<div class="pill-select${modifier ? ` pill-select--${modifier}` : ''}${
				labels.length ? ' has-selection' : ''
			}" data-hdt-placeholder="${escapeHtml(placeholder)}">
				<span class="pill-expander" data-hdt-action="dropdown-toggle" role="button" tabindex="0" aria-expanded="false">
					${summaryLabel(2)}
					${summaryLabel(1)}
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="512" height="512">
						<path fill="#FFF" d="M225.813 48.907L128 146.72 30.187 48.907 0 79.093l128 128 128-128z"/>
					</svg>
				</span>
				<div class="pills-expander" role="listbox" aria-multiselectable="true">
					${options
						.map(
							(option) =>
								`<label class="single-pill ${option.checked ? 'checked' : ''}" role="option" aria-selected="${
									option.checked ? 'true' : 'false'
								}" data-hdt-action="${itemAction}" ${option.attrs || ''}>${option.label}</label>`
						)
						.join('')}
				</div>
			</div>`;
		}

		renderQuestionHeading(subSection) {
			let html = '';

			if (subSection.external_alternative_question == null) {
				html += `<h2>${subSection.question || ''}</h2>`;
			} else {
				html += `<h2 class="hide--external">${subSection.question || ''}</h2>`;
				html += `<h2 class="hide--internal">${subSection.external_alternative_question}</h2>`;
			}

			if (subSection.question_description != null) {
				html += `<h3>${subSection.question_description}</h3>`;
			}

			if (subSection.show_article_heading != null) {
				html += `<div id="quiz-article-featured">${this.state.featuredArticlesHTML}</div>`;
			}

			return html;
		}

		renderAnswers(subSection) {
			const answers = subSection.answers || [];
			const sectionIndex = this.state.currentSection;
			const subIndex = this.state.currentSubSection;

			return `<section class="questions-wrapper">${answers
				.map((answer, answerIndex) => this.renderAnswer(sectionIndex, subIndex, answerIndex, answer))
				.join('')}</section>`;
		}

		renderAnswer(sectionIndex, subIndex, answerIndex, answer) {
			if (!this.tagShow(answer.tagged, answer.tag_show)) return '';

			const id = `q${sectionIndex}${subIndex}${answerIndex}`;
			const checkedClass = this.answerChecker(sectionIndex, subIndex, answerIndex, answer.checked, answer.answer_type) || '';
			const textContentClass = answer.answer_type === 'textcontent' ? ' textcontent ' : '';
			const widthStyle = answer.width ? `style="--box-width: ${answer.width};"` : '';
			const isChoice = answer.answer_type === 'checkbox' || answer.answer_type === 'radio';

			let inner = '';

			if (isChoice) {
				inner += `
					<label for="${id}" data-hdt-action="save-answer" data-section="${sectionIndex}" data-sub="${subIndex}" data-answer="${answerIndex}" data-type="${answer.answer_type}">
						${this.checkmarkHtml()}
						<h4>${answer.answer_title || ''}</h4>
						<p>${answer.answer_description || ''}</p>
					</label>
					<input type="${answer.answer_type}" id="${id}">
				`;
			} else {
				inner += `
					<label for="${id}">
						${answer.answer_type === 'textcontent' ? '<div class="quiz-textex">!</div>' : ''}
						<h4>${answer.answer_title || ''}</h4>
						<p>${answer.answer_description || ''}</p>
					</label>
				`;
			}

			if (answer.answer_type === 'pill-selector') {
				const options = (answer.values || []).map((pillvalue, pillIndex) => {
					const checked = this.pillChecker(sectionIndex, subIndex, answerIndex, pillIndex) === 'checked';

					if (checked) this.loadArticleTags(answer.articleAssociation, pillvalue[1]);

					return {
						label: pillvalue[0],
						checked,
						attrs: `data-value="${pillvalue[1]}" data-section="${sectionIndex}" data-sub="${subIndex}" data-answer="${answerIndex}" data-pill="${pillIndex}"`,
					};
				});

				inner += this.renderDropdown({
					placeholder: hdtT('quiz_select_concerns', 'Click to select all your health concerns:'),
					itemAction: 'pill-select',
					options,
				});
			}

			if (answer.answer_type === 'text' && answer.autocomplete && answer.autocomplete.length > 0) {
				inner += `
					<div class="autocomplete-outer">
						<input type="text" id="text_${sectionIndex}${subIndex}${answerIndex}" data-hdt-autocomplete data-section="${sectionIndex}" data-sub="${subIndex}" data-answer="${answerIndex}" data-autocomplete-list='${JSON.stringify(answer.autocomplete)}' class="${answer.articleAssociation ? 'articleAssociation' : ''}">
						<div class="autocomplete-wrapper${this.state.autoCompleteShow ? ' autoCompleteShow' : ''}">
							${this.state.autoCompleted
								.map(
									(item) =>
										`<span data-hdt-action="autocomplete-pick" data-value="${escapeHtml(item)}" data-section="${sectionIndex}" data-sub="${subIndex}" data-answer="${answerIndex}">${escapeHtml(item)}</span>`
								)
								.join('')}
						</div>
					</div>
				`;
			}

			return `<div class="single-question ${checkedClass} answers-input-wrapper${textContentClass}" id="sq${sectionIndex}${subIndex}${answerIndex}" ${widthStyle}>${inner}</div>`;
		}

		renderQuizNav() {
			if (!this.state.showPrevBack) return '';

			const { currentSection, currentSubSection } = this.state;
			const isFinish = currentSection === 2 && currentSubSection === 2;
			const nextLabel = isFinish
				? hdtT('quiz_fetch_articles', 'Fetch my articles')
				: hdtT('quiz_next', 'Next');
			const nextClass = isFinish
				? 'next finishquiz button button--gradient-green button--small'
				: 'next button button--gradient-green button--small';

			let html = `<div class="previous-next-wrap">`;

			if (currentSection > 0) {
				html += `<span class="prev button button--border-green white button--small" data-hdt-action="quiz-back">${hdtT('quiz_previous', 'Previous')}</span>`;
			}

			html += `<span class="${nextClass}" data-hdt-action="quiz-next">${nextLabel}</span>`;

			if (currentSection === 0 && currentSubSection === 0) {
				html += `<div class="disclaimer">${hdtT('quiz_disclaimer_agree', '*By clicking Next and using this tool I agree to')} <span data-hdt-action="disclaimer-open">${hdtT('quiz_this_disclaimer', 'this disclaimer')}</span></div>`;
			}

			if (currentSection > 0) {
				html += `<span class="reset-quiz-btn" data-hdt-action="quiz-reset">${hdtT('quiz_reset', 'Reset back to beginning')}</span>`;
			}

			html += `</div>`;

			return html;
		}

		renderComponent(name) {
			if (name === 'dog-details') return this.renderDogDetails();
			if (name === 'newsletter-details') return this.renderNewsletterDetails();
			if (name === 'quiz-end') return this.renderQuizEnd();
			if (name === 'article-fetcher') return `<div class="article-fetch-wrap"><h2>${hdtT('articles_to_help_heading', 'Articles to help keep your dog healthy:')}</h2><div id="article-build"></div></div>`;

			return '';
		}

		renderDogDetails() {
			const items = this.detailsTree.formItems || [];

			return `<div class="detailsFormInner">${items.map((item) => this.renderFormItem(item)).join('')}</div>`;
		}

		renderFormItem(item) {
			const saved = this.loadSavedDetails(item.id);
			const required = item.required ? 'required' : '';
			let field = '';

			if (item.type !== 'select' && item.type !== 'radio') {
				const extraClass = item.id === 'dogweight' ? ' class="sm-txt"' : '';
				const minMax = item.type === 'number' || item.id === 'dogweight' ? ` min="${item.min || ''}" max="${item.max || ''}"` : '';

				field = `<input placeholder="${item.placeholder || ''}"${extraClass} value="${saved || ''}" type="${item.type}" ${required} data-hdt-field="${item.id}"${minMax}>`;

				if (item.id === 'dogweight') {
					field += `<div class="lbsorkgs">
						<span class="${this.state.userDetails.dogweight_type === 'lb' ? 'active' : ''}" data-hdt-action="update-weight-type" data-value="lb">${hdtT('lbs', 'LBS')}</span>
						${hdtT('or', 'or')}
						<span class="${this.state.userDetails.dogweight_type === 'kg' ? 'active' : ''}" data-hdt-action="update-weight-type" data-value="kg">${hdtT('kgs', 'KGS')}</span>
					</div>`;
				}
			}

			if (item.type === 'radio') {
				field = `<div class="radio-details">${(item.values || [])
					.map(
						(val) => `
						<div class="radio-details-single ${this.loadSavedDetails(item.id, 'radio', val)}" data-hdt-action="save-radio-detail" data-key="${item.id}" data-value="${val}">
							<input type="radio">
							${this.checkmarkHtml()}
							${val}
						</div>`
					)
					.join('')}</div>`;
			}

			if (item.type === 'select' && item.multiselect !== true) {
				field = `<select data-hdt-field="${item.id}" class="needsclick">
					${(item.values || [])
						.map((val) => `<option value="${val}" ${this.loadSavedDetails(item.id, 'select', val)}>${val}</option>`)
						.join('')}
				</select>`;
			}

			if (item.type === 'select' && item.multiselect === true) {
				const values = item.values || [];
				const selected = Array.isArray(this.state.userDetails[item.id]) ? this.state.userDetails[item.id] : [];

				// No id on the select - the dashboard sidebar renders one for the same field
				field = `${this.renderDropdown({
					placeholder: hdtT('select_all_food_types_you_feed', 'Select all food types you feed'),
					itemAction: 'update-food',
					options: values.map((val) => ({ label: val, checked: selected.includes(val), attrs: `data-value="${escapeHtml(val)}"` })),
				})}
				<select data-hdt-field="${item.id}" multiple class="needsclick hdt-visually-hidden" aria-hidden="true" tabindex="-1">
					${values.map((val) => `<option value="${val}" ${selected.includes(val) ? 'selected' : ''}>${val}</option>`).join('')}
				</select>`;
			}

			return `<div class="half"><label>${item.label}</label>${field}</div>`;
		}

		renderNewsletterDetails() {
			const items = this.detailsTree.userPersonalInputs || [];

			return `<div class="detailsFormInner"><form>${items
				.map((item) => {
					if (item.type === 'select') return '';

					return `<div class="half">
						<label>${item.label}</label>
						<input placeholder="${item.placeholder || ''}" type="${item.type}" id="newsletter_${item.id}" value="${this.loadSavedDetails(item.id) || ''}" ${item.required ? 'required' : ''} data-hdt-field="${item.id}" data-newsletter-field="${item.newsletter_field || ''}">
					</div>`;
				})
				.join('')}</form></div>`;
		}

		renderQuizEnd() {
			const dogname = this.state.userDetails.dogname || '';

			return `<div id="lastquestion">
				<h2>${interpolateDogName(hdtT('quiz_end_congrats'), dogname)}</h2>
				<div class="peterQuote">
					<div class="peterQuoteImg" style="background-image: url(${DOG_DEFAULT_SRC})"></div>
				</div>
				<p>${interpolateDogName(hdtT('quiz_end_recommended_question'), dogname)}</p>
				<div class="ssqbpButtons">
					<span class="pisp-previous button button--border-green white button--full" data-hdt-action="toggle-quiz-modal">${hdtT('quiz_end_no', 'No')}</span>
					<span class="pisp-next button button--gradient-green button--full" id="fouressentials" data-hdt-action="intro-show">${hdtT('quiz_end_yes', 'Yes')}</span>
				</div>
				<div class="loading-modal loading-modal-quiz is-hidden">
					<div class="loading-modal-quiz__wrapper">
						<p>${hdtT('quiz_end_are_you_sure', 'Are you sure? You can always opt out later')}</p>
						<div class="loading-modal-quiz__buttons-wrapper">
							<a href="/blogs/video" class="pisp-previous button button--border-green white button--small">${hdtT('quiz_end_yes_i_am_sure', 'Yes, I am sure')}</a>
							<span class="pisp-next button button--gradient-green button--full" data-hdt-action="intro-show">${hdtT('quiz_end_ok_peek', 'Ok, I want to have a peek')}</span>
						</div>
					</div>
				</div>
			</div>`;
		}

		onQuizEndCreated() {
			this.state.showPrevBack = false;
			this.state.showQuizHeading = false;

			const issues = document.querySelector('#doghealthissues');

			if (issues) {
				issues.value = !this.state.articleTags.length ? 'none' : this.state.articleTags;
			}

			const firstName = document.querySelector('#firstname_newsletter');
			const email = document.querySelector('#email_newsletter');

			if (firstName && !firstName.value) firstName.value = this.state.userDetails.username || '';
			if (email && !email.value) email.value = this.state.userDetails.emailaddress || '';

			document.querySelector('#_form_19_submit')?.click();
			this.syncDashboard();
		}

		showDashboardIntro() {
			this.setView('essentials');
			postIframeHeight(50);
		}

		loadFeaturedArticles(items) {
			this.state.featuredArticlesHTML = '';

			(items || []).forEach((item) => {
				fetch(encodeURI(`/blogs/blog/${item.article}?view=article-hdt-dont-use`))
					.then((resp) => resp.json())
					.then((data) => {
						data = data[0];

						const sectionId = document.querySelector('#main-content .shopify-section')?.id.replace('shopify-section-', '') || 'hdt';
						const videoId = String(item.video_id || '').replace('youtube:', '');
						const htmlOut = `
							<div class="background-video__content" style="background-image: url(${data.image})">
								<modal-opener class="video-lightbox-modal__opener no-js-hidden" data-modal="#VideoLightbox-${sectionId}">
									<button id="VideoLightboxToggle-${sectionId}" class="video-lightbox-modal__button" type="button" aria-haspopup="dialog" aria-label="Play video">
										<svg width="40" height="47" viewBox="0 0 40 47" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M2.61399 0.236885L39.14 21.5449C39.4018 21.6981 39.6189 21.9172 39.7698 22.1803C39.9206 22.4435 40 22.7415 40 23.0448C40 23.3481 39.9206 23.6462 39.7698 23.9093C39.6189 24.1724 39.4018 24.3915 39.14 24.5447L2.61399 45.8527C2.34935 46.0072 2.04859 46.0889 1.74218 46.0896C1.43578 46.0904 1.13463 46.0101 0.869257 45.8569C0.60388 45.7038 0.383708 45.4832 0.231056 45.2175C0.0784043 44.9518 -0.00130198 44.6505 1.60855e-05 44.3441V1.73853C-7.45482e-05 1.43272 0.0805262 1.1323 0.233678 0.8676C0.386831 0.602904 0.607113 0.383304 0.872282 0.230972C1.13745 0.0786409 1.43812 -0.00102811 1.74393 1.00171e-05C2.04974 0.00104815 2.34986 0.0827564 2.61399 0.236885Z" fill="white"/>
										</svg>
									</button>
								</modal-opener>
								<modal-dialog id="VideoLightbox-${sectionId}" class="video-lightbox-modal">
									<div role="dialog" aria-modal="true" class="video-lightbox-modal__content" tabindex="-1">
										<button id="ModalClose-${sectionId}" type="button" class="video-lightbox-modal__toggle" aria-label="Close">
											<svg clip-rule="evenodd" fill-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m12 10.93 5.719-5.72c.146-.146.339-.219.531-.219.404 0 .75.324.75.749 0 .193-.073.385-.219.532l-5.72 5.719 5.719 5.719c.147.147.22.339.22.531 0 .427-.349.75-.75.75-.192 0-.385-.073-.531-.219l-5.719-5.719-5.719 5.719c-.146.146-.339.219-.531.219-.401 0-.75-.323-.75-.75 0-.192.073-.384.22-.531l5.719-5.719-5.72-5.719c-.146-.147-.219-.339-.219-.532 0-.425.346-.749.75-.749.192 0 .385.073.531.219z"/></svg>
										</button>
										<div class="video-lightbox-modal__content-info">
											<iframe data-src="https://www.youtube.com/embed/${videoId}?enablejsapi=1" class="js-youtube lazyload" allow="autoplay; encrypted-media" allowfullscreen></iframe>
										</div>
									</div>
								</modal-dialog>
							</div>
							<h3 class="h4">${hdtT('featured_video_heading', 'Watch the Healing Cycle Video:')} <a href="/blogs/blog/${item.article}/" class="iframe-modal fha">${hdtT('featured_video_link_text', 'The Healing Cycle - How to keep your dog healthy, happy and long living')}</a></h3>`;

						this.state.featuredArticlesHTML += htmlOut;

						const holder = this.quizEl.querySelector('#quiz-article-featured');

						if (holder) holder.innerHTML = this.state.featuredArticlesHTML;
					})
					.catch((error) => console.log('featured article error', error));
			});

			postIframeHeight(100);
		}

		fetchArticles() {
			const articleBuild = this.quizEl.querySelector('#article-build');

			if (!articleBuild) return;

			const signature = this.state.articleTags.join('|');

			// The list only depends on the selected tags, so a re-render of the same step
			// paints the previous result instead of hitting the search endpoint again
			if (this.articlesCache && this.articlesCache.signature === signature) {
				this.paintArticles(this.articlesCache.articles, signature);

				return;
			}

			const urls = this.state.articleTags
				.filter((tag) => tag.toLowerCase() !== 'general articles')
				.map(
					(tag) =>
						`/search/suggest.json?q=${tag.replace(/ /g, '%20')}&resources[type]=article&resources[limit_scope]=each&resources[limit]=10`
				);

			if (!urls.length) {
				this.paintArticles(window.store.recommendedArticles || [], signature);

				return;
			}

			Promise.all(urls.map((url) => fetch(url)))
				.then((responses) => Promise.all(responses.map((res) => res.json())))
				.then((texts) => texts.map((el) => el.resources.results.articles))
				.then((data) => {
					const articles = data.flat().filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

					this.paintArticles([...(window.store.recommendedArticles || []), ...articles], signature);
				})
				.catch((error) => console.log('article fetch error', error));
		}

		paintArticles(articles, signature) {
			const articleBuild = this.quizEl.querySelector('#article-build');
			const wrap = this.quizEl.querySelector('.article-fetch-wrap');

			if (!articleBuild) return;

			this.articlesCache = { signature, articles };
			this.state.userDetails.articlesForEmail = articles.map(({ title, url, image }) => ({ title, url, image }));

			articleBuild.innerHTML = articles
				.map(
					(article) =>
						`<article><h4><span class="featured-img" style="background-image:url(${
							article.image || PLACEHOLDER_ARTICLE_IMG
						})"></span><a class="org-circle" target="_blank" href="${article.url}"><span class="featured-text">${
							article.title
						}</span></a></h4></article>`
				)
				.join('');

			if (wrap) wrap.style.display = 'block';

			this.persistDetails();
		}

		weekTitle(week) {
			const parsed = parseInt(week, 10);
			const titles = {
				1: hdtT('week_first', 'The First Week'),
				2: hdtT('week_second', 'The Second Week'),
				3: hdtT('week_third', 'The Third Week'),
				4: hdtT('week_fourth', 'The Fourth Week'),
				5: hdtT('week_fifth', 'The Fifth Week'),
			};

			return titles[parsed] || '';
		}

		weightInLb() {
			const weight = parseInt(this.state.userDetails.dogweight, 10) || 0;

			if (this.state.userDetails.dogweight_type === 'kg') {
				return Math.round(weight / 0.45359237);
			}

			return weight;
		}

		matchDosage(handle) {
			const dogWeight = this.weightInLb();
			const product = (this.dosages.products || []).find((item) => item.handle === handle);

			if (!product) return '';

			for (let j = 0; j < product.dosage_details.length; j += 1) {
				const [min, max] = product.dosage_details[j].weight.split('-').map((part) => parseInt(part, 10));

				if (dogWeight >= min && dogWeight <= max) {
					const daily = product.dosage_details[j].daily_dose;

					if (daily.indexOf('/') !== -1) {
						const topfrac = daily[daily.indexOf('/') - 1];
						const botfrac = daily[daily.indexOf('/') + 1];
						const out = daily.replace(/[0-9]/g, '').replace(/\//g, '');

						return `&frac${topfrac}${botfrac} ${out}`;
					}

					return daily;
				}
			}

			return '';
		}

		dosageLife(handle) {
			const dogWeight = this.weightInLb();
			const product = (this.dosages.products || []).find((item) => item.handle === handle);

			if (!product) return '';

			for (let j = 0; j < product.dosage_details.length; j += 1) {
				const [min, max] = product.dosage_details[j].weight.split('-').map((part) => parseInt(part, 10));

				if (dogWeight < min || dogWeight > max) continue;

				const productLife = parseInt(product.dosage_details[j].product_life, 10);
				const productMonths = Math.floor(productLife / 30);
				const productWeeks = Math.floor(4 * (productLife / 30 - Math.floor(productLife / 30)));
				const productDays = Math.floor(
					7 * (4 * (productLife / 30 - Math.floor(productLife / 30)) - Math.floor(4 * (productLife / 30 - Math.floor(productLife / 30))))
				);
				let productLifeOut = '';

				if (productMonths > 0) {
					productLifeOut += `${productMonths} ${productMonths === 1 ? hdtT('month', 'Month') : hdtT('months', 'Months')}<br>`;
				}

				if (productWeeks > 0) {
					productLifeOut += `${productWeeks} ${productWeeks === 1 ? hdtT('week', 'Week') : hdtT('weeks', 'Weeks')}<br>`;
				}

				if (productDays > 0) {
					productLifeOut += `${productDays} ${productDays === 1 ? hdtT('day', 'Day') : hdtT('days', 'Days')}<br>`;
				}

				const parsed = this.state.productsParsed.find((item) => item.handle === handle);

				if (!parsed) return '';

				return `<div class="pil-content">
					<img src="${parsed.featured_image}">
					<strong>1 ${parsed.title.split(' - ')[0].split(' ')[0]}</strong>
					<span>${productLifeOut}</span>
				</div>`;
			}

			return '';
		}

		grabProductData(handle) {
			const productMeta = (this.dosages.products || []).find((item) => item.handle === handle);
			const isOos = productMeta?.available === false;
			const parsed = this.state.productsParsed.find((item) => item.handle === handle);

			if (!parsed) return '';

			let productType = hdtT('product_type_supplements', 'Supplements');

			for (let t = 0; t < PRODUCT_TYPE_TAGS.length; t += 1) {
				if (parsed.tags.includes(PRODUCT_TYPE_TAGS[t])) {
					productType = PRODUCT_TYPE_TAGS[t];
					break;
				}
			}

			return `<div class="wpd-image"><img src="${parsed.featured_image}" alt=""></div>
				<div class="wpd-body">
					<div class="wpd-product-info" data-product-link="${parsed.url}">
						<h4>${parsed.title.split(' - ')[0]}</h4>
						${isOos ? `<span class="hdt-oos">${hdtT('out_of_stock', 'Out of stock')}</span>` : ''}
						<span>${productType}</span>
					</div>
					<div class="wpd-product-dosage">
						<strong>${this.matchDosage(handle)}</strong>
						<p><img src="${CLOCK_SRC}" alt=""> ${hdtT('daily_mix_in_food', 'daily - mix in food')}</p>
					</div>
				</div>`;
		}

		renderWeekProduct(product, weekName) {
			if (!product.week_assoc || !product.week_assoc.includes(weekName)) return '';
			if (product.handle === 'livertune-supplement' && (weekName === '01' || !this.state.liverCleanseShow)) return '';

			return `<div class="week-product-dosage">${this.grabProductData(product.handle)}</div>`;
		}

		renderDosages() {
			const weeksRoot = this.dashboardEl.querySelector('[data-hdt-weeks]');
			const lifeRoot = this.dashboardEl.querySelector('[data-hdt-product-life]');

			if (weeksRoot) {
				weeksRoot.innerHTML = (this.dosages.weeks || [])
					.map((week) => {
						const productsHtml = (this.dosages.products || [])
							.map((product) => this.renderWeekProduct(product, week.week_name))
							.join('');

						return `<div class="week-dosage-single">
							<div class="week-dosages-heading">
								<div class="dosage-week-description">
									<h3>${this.weekTitle(week.week_name)}</h3>
									${
										week.week_name == '01'
											? `<p>${hdtT('start_with_supplements', 'Start with the following supplements by mixing them in')} <span data-hdt-bind="dogname">${escapeHtml(this.state.userDetails.dogname || '')}</span>${hdtT('s_food_or_serve', "'s food or serve them in a small amount of tasty treat or food if your dog is picky. GutSense comes in capsule form - you can either give the whole capsule or open the capsule and mix in food.")}</p>`
											: `<p>${week.week_description || ''}</p>`
									}
								</div>
								<div class="dosage-week">
									<div class="dosage-week-inner">
										<span>${hdtT('week_label', 'Week')}</span>
										<strong>${week.week_name}</strong>
									</div>
								</div>
							</div>
							<div class="week-product-dosages">
								<div class="hdt-dosage-track">${productsHtml}</div>
								<div class="next-dosage-product" data-hdt-action="scroll-next">></div>
							</div>
						</div>`;
					})
					.join('');
			}

			if (lifeRoot) {
				lifeRoot.innerHTML = (this.dosages.products || [])
					.map((product) => {
						if (product.handle === 'livertune-supplement' && !this.state.liverCleanseShow) return '';

						return `<div class="pil-product">${this.dosageLife(product.handle)}</div>`;
					})
					.join('');
			}

			this.syncDosageTracks();
			this.syncDashboardBindings();
		}

		// Weeks that fit entirely hide their arrow and fade, the rest dim the arrow at the end.
		// Motion is a translate3d on the track, same idea as the old Swiper - native overflow
		// painted a Windows scrollbar and scrollLeft is unreliable in embedded Chrome.
		syncDosageTracks() {
			this.dashboardEl.querySelectorAll('.week-product-dosages').forEach((track) => {
				const scroller = track.querySelector('.hdt-dosage-track');

				if (!scroller) return;

				this.applyDosageOffset(scroller, this.dosageOffset(scroller));
				this.bindDosageDrag(scroller);
			});
		}

		dosageOffset(scroller) {
			return Number(scroller.dataset.offset || 0);
		}

		maxDosageOffset(scroller) {
			const parent = scroller.parentElement;

			if (!parent) return 0;

			return Math.max(0, scroller.scrollWidth - parent.clientWidth);
		}

		applyDosageOffset(scroller, offset) {
			const max = this.maxDosageOffset(scroller);
			const next = Math.min(max, Math.max(0, offset));
			const track = scroller.closest('.week-product-dosages');

			scroller.dataset.offset = String(next);
			scroller.style.transform = next ? `translate3d(-${next}px, 0, 0)` : '';

			track?.classList.toggle('is-scrollable', max > 1);
			track
				?.querySelector('.next-dosage-product')
				?.classList.toggle('swiper-button-disabled', next >= max - 1);
		}

		bindDosageDrag(scroller) {
			if (scroller.dataset.dragReady === 'true') return;

			scroller.dataset.dragReady = 'true';

			let startX = 0;
			let startOffset = 0;
			let dragging = false;

			scroller.addEventListener('pointerdown', (event) => {
				if (event.pointerType === 'mouse' && event.button !== 0) return;

				dragging = true;
				startX = event.clientX;
				startOffset = this.dosageOffset(scroller);
				scroller.classList.add('is-dragging');
				scroller.setPointerCapture(event.pointerId);
			});

			scroller.addEventListener('pointermove', (event) => {
				if (!dragging) return;

				this.applyDosageOffset(scroller, startOffset - (event.clientX - startX));
			});

			const stopDrag = () => {
				if (!dragging) return;

				dragging = false;
				scroller.classList.remove('is-dragging');
			};

			scroller.addEventListener('pointerup', stopDrag);
			scroller.addEventListener('pointercancel', stopDrag);
		}

		syncDashboard() {
			this.syncDashboardBindings();
			this.syncDashboardToggles();
			this.syncSidebar();
			this.renderDosages();
			this.renderSidebarFoodOptions();
			this.renderSidebarBreeds();
		}

		syncDashboardBindings() {
			const details = this.state.userDetails;
			const himHer = details.doggender === 'Female' ? hdtT('her', 'her') : hdtT('him', 'him');
			const foodDisplay = Array.isArray(details.dogfoodtype) ? details.dogfoodtype.join(', ') : details.dogfoodtype || '';
			const map = {
				dogname: details.dogname || '',
				emailaddress: details.emailaddress || '',
				himHer,
				dogweight: details.dogweight || '',
				dogweight_type: details.dogweight_type || '',
				dogbreedtype: details.dogbreedtype || '',
				dogfoodtypeDisplay: foodDisplay,
				dogage: details.dogage || '',
				doggender: details.doggender || '',
				dognuetered: details.dognuetered || '',
			};

			this.root.querySelectorAll('[data-hdt-bind]').forEach((el) => {
				el.textContent = map[el.dataset.hdtBind] || '';
			});

			const emailTpl = this.dashboardEl.querySelector('#emailtpl');

			if (emailTpl) {
				emailTpl.setAttribute('data-user-name', details.username || '');
				emailTpl.setAttribute('data-dog-name', details.dogname || '');
			}
		}

		syncDashboardToggles() {
			this.root.classList.toggle('is-liver-cleanse', this.state.liverCleanseShow);
			this.dashboardEl.querySelector('#yescleanse .sq-checkbox')?.classList.toggle('checked', this.state.liverCleanseShow);
			this.dashboardEl.querySelector('#nocleanse .sq-checkbox')?.classList.toggle('checked', this.state.liverCleanseHide);

			this.dashboardEl.querySelectorAll('[data-hdt-show]').forEach((el) => {
				el.hidden = !this.state[el.dataset.hdtShow];
			});
			this.dashboardEl.querySelectorAll('[data-hdt-hide]').forEach((el) => {
				el.hidden = !!this.state[el.dataset.hdtHide];
			});

			this.dashboardEl.querySelectorAll('[data-hdt-checked]').forEach((el) => {
				el.classList.toggle('checked', !!this.state[el.dataset.hdtChecked]);
			});
		}

		syncSidebar() {
			const list = this.dashboardEl.querySelector('.dog-details-list-wrapper ul');

			if (!list) return;

			list.classList.toggle('activeInputs', this.state.updateDetailInputs);
			list.classList.toggle('collapse', this.state.collapseDetails);

			this.dashboardEl.querySelectorAll('[data-hdt-edit-only]').forEach((el) => {
				el.hidden = !this.state.updateDetailInputs;
			});
			this.dashboardEl.querySelectorAll('[data-hdt-view-only]').forEach((el) => {
				el.hidden = this.state.updateDetailInputs;
			});

			const weightInput = this.dashboardEl.querySelector('[data-hdt-field="dogweight"]');
			const ageInput = this.dashboardEl.querySelector('[data-hdt-field="dogage"]');

			if (weightInput && document.activeElement !== weightInput) weightInput.value = this.state.userDetails.dogweight || '';
			if (ageInput && document.activeElement !== ageInput) ageInput.value = this.state.userDetails.dogage || '';

			const editLabel = this.dashboardEl.querySelector('[data-hdt-edit-label]');

			if (editLabel) {
				editLabel.textContent = this.state.updateDetailInputs
					? hdtT('save_details', 'Save Details')
					: hdtT('edit_details', 'Edit Details');
			}

			const collapseLabel = this.dashboardEl.querySelector('[data-hdt-collapse-label]');

			if (collapseLabel) {
				collapseLabel.textContent = this.state.collapseDetails
					? hdtT('expand', 'Expand')
					: hdtT('collapse', 'Collapse');
			}

			this.dashboardEl.querySelectorAll('[data-hdt-action="update-weight-type"]').forEach((el) => {
				el.classList.toggle('active', el.dataset.value === this.state.userDetails.dogweight_type);
			});

			this.dashboardEl.querySelectorAll('[data-hdt-action="update-gender"]').forEach((el) => {
				el.classList.toggle('activeSB', el.dataset.value === this.state.userDetails.doggender);
				const input = el.querySelector('input');

				if (input) input.checked = el.dataset.value === this.state.userDetails.doggender;
			});

			this.dashboardEl.querySelectorAll('[data-hdt-action="update-neutered"]').forEach((el) => {
				el.classList.toggle('activeSB', el.dataset.value === this.state.userDetails.dognuetered);
				const input = el.querySelector('input');

				if (input) input.checked = el.dataset.value === this.state.userDetails.dognuetered;
			});
		}

		renderSidebarBreeds() {
			const select = this.dashboardEl.querySelector('[data-hdt-breed-select]');

			if (!select || select.dataset.ready === 'true') return;

			const breeds = this.dogBreeds.dogBreeds || [];

			select.innerHTML = breeds
				.map(
					(option) =>
						`<option value="${option}" ${option === this.state.userDetails.dogbreedtype ? 'selected' : ''}>${option}</option>`
				)
				.join('');
			select.dataset.ready = 'true';
		}

		renderSidebarFoodOptions() {
			const holder = this.dashboardEl.querySelector('[data-hdt-food-dropdown]');
			const select = this.dashboardEl.querySelector('#dogfoodtype');
			const foods = this.dogFood.dogFood || [];
			const selected = Array.isArray(this.state.userDetails.dogfoodtype) ? this.state.userDetails.dogfoodtype : [];

			// Built once, then only synced - rebuilding would close the dropdown mid-interaction
			if (holder && holder.dataset.ready !== 'true') {
				holder.innerHTML = this.renderDropdown({
					modifier: 'compact',
					placeholder: hdtT('dashboard_select_food_types', 'Select all food types you feed'),
					itemAction: 'update-food',
					options: foods.map((val) => ({ label: val, checked: selected.includes(val), attrs: `data-value="${escapeHtml(val)}"` })),
				});
				holder.dataset.ready = 'true';
			}

			if (select && !select.options.length) {
				select.innerHTML = foods.map((val) => `<option value="${val}">${val}</option>`).join('');
			}

			this.syncFoodSelection(selected);
		}

		// Keeps every food dropdown (quiz form and dashboard sidebar) and their hidden selects aligned
		syncFoodSelection(selected) {
			this.root.querySelectorAll('.pill-select').forEach((wrap) => {
				if (!wrap.querySelector('[data-hdt-action="update-food"]')) return;

				this.syncDropdown(wrap, (item) => selected.includes(item.dataset.value));
			});

			this.root.querySelectorAll('[data-hdt-field="dogfoodtype"]').forEach((select) => {
				Array.from(select.options).forEach((option) => {
					option.selected = selected.includes(option.value);
				});
			});
		}

		setLiverCleanse(show) {
			this.state.liverCleanseShow = show;
			this.state.liverCleanseHide = !show;
			this.syncDashboardToggles();
			this.renderDosages();
			postIframeHeight(50);
		}

		handleStepNav(item) {
			const goTo = item.getAttribute('data-go-to');

			if (goTo === 'quiz') this.setView('quiz');
			else if (goTo === 'essentials') this.setView('essentials');
			else if (goTo === 'dosages') this.setView('dosages');
		}

		toggleMobileSidebar(button) {
			const current = button.textContent;
			const toggle = button.getAttribute('data-toggle-txt');

			button.textContent = toggle;
			button.setAttribute('data-toggle-txt', current);
			this.dashboardEl.querySelector('.pis-sidebar-details')?.classList.toggle('mobile-slide');
			this.dashboardEl.classList.toggle('mobile-slide');
		}

		toggleProductDetail(el, open) {
			const product = el.closest('.pis-ip-product');
			const list = el.closest('.pis-ip-products');

			if (!product || !list) return;

			if (open) {
				product.classList.add('activated');
				list.classList.add('product-detailer');
			} else {
				product.classList.remove('activated');
				list.classList.remove('product-detailer');
			}
		}

		switchEssentialProduct(img) {
			const handle = img.getAttribute('data-product-target');
			const list = this.dashboardEl.querySelector('.pis-ip-products');

			if (!list) return;

			list.querySelectorAll('.activated').forEach((item) => item.classList.remove('activated'));
			list.querySelector(`.pis-ip-product[data-product-handle="${handle}"]`)?.classList.add('activated');
		}

		loadEssentialProducts() {
			const products = this.dosages.products || [];
			const essentialsRoot = this.dashboardEl.querySelector('[data-hdt-essentials]');

			Promise.all(
				products.map((product) =>
					fetch(`/products/${product.handle}.js`).then((response) => {
						if (!response.ok) throw new Error(response.status);

						return response.json();
					})
				)
			)
				.then((dataList) => {
					let essentialsHtml = '';

					dataList.forEach((data) => {
						const meta = products.find((item) => item.handle === data.handle);
						const essential = meta?.essential === true;
						const productDescription = meta?.description || '';

						this.state.productsParsed.push(data);

						if (!essential) return;

						essentialsHtml += `<div class="pis-ip-product product-cart-item-small" data-product-handle="${data.handle}">
							<div class="product-cart-item-image">
								<a href="${data.url}" title="${data.title}">
									<img class="image--horizontal lazyautosizes" width="1627" height="1620" src="${data.featured_image}" alt="${data.title}">
								</a>
							</div>
							<div class="product-cart-item-details">
								<a href="${data.url}" title="${data.title}" class="cart-product-link">${data.title}</a>
								<span class="cart-product-description">${productDescription.split('.')[0]}.</span>
								<a href="#" class="product-card-quickview" title="${hdtT('quick_view', 'Quick View')}" data-handle="${data.handle}">${hdtT('more_details', 'More Details')}</a>
								<div class="toggle-description is-hidden">${productDescription}</div>
							</div>
						</div>`;
					});

					if (essentialsRoot) essentialsRoot.innerHTML = essentialsHtml;
					this.essentialsLoaded = true;
					this.renderDosages();
				})
				.catch((error) => console.log('product fetch error', error));
		}

		openQuickView(evt) {
			evt.preventDefault();

			const quickviewButton = evt.target.closest('.product-card-quickview');
			const productHandle = quickviewButton?.dataset.handle;
			let href = `${window.theme.routes.root_url}products/${productHandle}?view=quick-view-long`;
			const drawer = document.getElementById('Product-Drawer');
			const description = quickviewButton.parentElement.querySelector('.toggle-description')?.textContent;

			href = href.replace('//', '/');

			if (!href || !productHandle || quickviewButton.classList.contains('loading')) return;

			quickviewButton.classList.add('loading');

			fetch(href, { method: 'GET' })
				.then((response) => {
					quickviewButton.classList.remove('loading');

					return response.text();
				})
				.then((text) => {
					const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('#Product-Drawer-Content').innerHTML;
					const newSectionInnerHTML = sectionInnerHTML.replace('[[description]]', description);

					drawer.querySelector('#Product-Drawer-Content').innerHTML = newSectionInnerHTML;
					document.body.classList.add('open-cc');
					drawer.classList.add('active');
					drawer.querySelector('.side-panel-close')?.focus();

					if (drawer.querySelector('variant-selects')) {
						drawer.querySelector('variant-selects').onVariantChange();
					} else if (typeof window.modifyProductPrices === 'function') {
						window.modifyProductPrices();
					}
				});
		}

		handleOrderNext() {
			if (this.state.sendEmail) this.sendPlanEmail();
			if (this.state.addToCart) this.orderProducts();
			this.setView('ordered');
		}

		onFinishQuiz() {
			const userDetails = getStorageJson('pd_user_details', this.state.userDetails);

			if (userDetails.to_subscribe === undefined) {
				userDetails.to_subscribe = true;
				this.state.userDetails.to_subscribe = true;
				this.persistDetails();
			}

			if (userDetails.to_subscribe === true && userDetails.username != '' && userDetails.emailaddress != '') {
				this.subscribeKlaviyo(userDetails);
			}

			if (userDetails.username != '' && userDetails.emailaddress != '') {
				this.sendArticlesEmail(userDetails);
			}
		}

		subscribeKlaviyo(userDetails) {
			const email = String(userDetails.emailaddress || '').trim();

			if (!email) return;

			// The app proxy subscribes the customer in Shopify (when they already exist there)
			// and always adds them to the Healthy Dog Tool Klaviyo list.
			fetch(SUBSCRIBE_API_PATH, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify({
					email,
					name: String(userDetails.username || '').trim(),
					dogName: String(userDetails.dogname || '').trim(),
				}),
			})
				.then((response) => response.json())
				.then((data) => {
					console.log(data && data.success ? 'added to newsletter' : 'error');
				})
				.catch((error) => console.log('error', error));
		}

		sendArticlesEmail(userDetails) {
			const individualRow = ({ title, url, image }) => `<tr width="100%" style="padding: 20px; border-bottom: 1px solid #727272; display: block;">
				<td align="left" height="100%" scope="col" width="100">
					<a href="https://peterdobias.com${url}" style="color: #727272 !important; text-decoration: none; display: block; ">
						<img src="${image && image.includes('https') ? image : 'https:' + (image || '')}" width="100" height="auto" style="vertical-align: middle; border-radius: 7px; line-height: 100%; outline: none;"/>
					</a>
				</td>
				<td align="left" height="100%" scope="col">
					<a href="https://peterdobias.com${url}" style="color: #727272 !important; text-decoration: none; font-size: 16px; display: block; margin-left: 15px; font-family: Trebuchet MS, sans-serif;">
						${title}
					</a>
				</td>
			</tr>`;

			let articlesRowsMarkup = `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; padding: 0 0 20px 0; line-height: 1.4; border-collapse: collapse !important"><tbody>`;

			(userDetails.articlesForEmail || []).forEach((article) => {
				articlesRowsMarkup += individualRow(article);
			});
			articlesRowsMarkup += `</tbody></table>`;

			const dataForEmail = {
				dog_name: escapeHtml(userDetails.dogname),
				dog_health_concerns:
					(window.store.recommendedArticles || []).length != (userDetails.articlesForEmail || []).length
						? ' life and also address your main concerns'
						: '',
				articles_html: articlesRowsMarkup,
				client_email: escapeHtml(userDetails.emailaddress),
			};

			const sendEmail = (payload) => {
				if (!window.emailjs) return;

				emailjs.init(EMAILJS_PUBLIC_KEY);
				emailjs.send('default_service', 'template_9uo66iz', payload).then(
					() => {
						payload.timeStamp = Date.now();
						const emailCookieNew = btoa(unescape(encodeURIComponent(JSON.stringify(payload)).replace(/(?:\\[rn])+/g, '')));

						sessionStorage.setItem('pd_eml', emailCookieNew);
					},
					(err) => {
						throw new Error(err);
					}
				);
			};

			let emailCookie = sessionStorage.getItem('pd_eml');

			if (emailCookie) {
				const decodedBtoaa = atob(emailCookie);
				const textDecoder = new TextDecoder('utf-8');
				const decodedString = textDecoder.decode(new Uint8Array(decodedBtoaa.split('').map((c) => c.charCodeAt(0))));

				emailCookie = JSON.parse(decodedString);

				const timeDifferenceInMinutes = Math.floor((Date.now() - emailCookie.timeStamp) / 60000);
				const samePayload =
					emailCookie.client_email === dataForEmail.client_email &&
					emailCookie.dog_name === dataForEmail.dog_name &&
					emailCookie.dog_health_concerns === dataForEmail.dog_health_concerns &&
					emailCookie.articles_html === dataForEmail.articles_html;

				if (timeDifferenceInMinutes <= 5 && samePayload) {
					console.log('email already sent');

					return;
				}
			}

			sendEmail(dataForEmail);
		}

		applyEmailInlineStyles(root) {
			const assignStyles = (selector, styles) => {
				root.querySelectorAll(selector).forEach((el) => Object.assign(el.style, styles));
			};

			assignStyles('.remainder-wrap', {
				width: '100%',
				textAlign: 'center',
				fontSize: '16px',
				borderBottom: 'solid 1px rgba(0,0,0,.15)',
				paddingBottom: '36px',
				marginBottom: '36px',
			});
			assignStyles('.emailheading', { width: '100%', fontSize: '24px' });
			assignStyles('.week-dosage-single', {
				width: '100%',
				marginBottom: '36px',
				borderBottom: 'solid 1px rgba(0,0,0,.15)',
				paddingBottom: '36px',
			});
			assignStyles('.week-product-dosages', { width: '100%' });
			assignStyles('.week-product-dosage', { width: '33%', display: 'inline-block', verticalAlign: 'top' });
			assignStyles('.wpd-image', { width: '100px', display: 'inline-block', verticalAlign: 'middle' });
			assignStyles('.wpd-body', { display: 'inline-block', width: 'calc(100% - 110px)', verticalAlign: 'top', padding: '8px' });
			assignStyles('.pil-product', { width: '33%', display: 'inline-block' });
			assignStyles('.wpd-product-info h4', { fontSize: '18px', width: '100%', marginBottom: '0px', marginTop: '0px' });
			assignStyles('.wpd-product-info span', { fontSize: '16px', width: '100%', marginBottom: '8px', fontWeight: 'light' });
			assignStyles('.week-product-dosage .wpd-product-dosage strong', { fontSize: '16px', width: '100%', fontStyle: 'italic' });
			assignStyles('.wpd-product-info p', { fontSize: '16px', width: '100%', margin: '0px' });
			assignStyles('.week-product-dosage .wpd-product-dosage p', { fontSize: '16px', width: '100%', margin: '0px' });
			assignStyles('.wpd-product-dosage p img', { height: '16px', width: 'auto', marginRight: '8px' });
			assignStyles('.product-intro-length', { width: '100%', marginTop: '0px', marginBottom: '56px', textAlign: 'center' });
			assignStyles('.dosage-week-description h3', { fontSize: '20px', textTransform: 'uppercase', marginBottom: '0px' });
			assignStyles('.dosage-week-description p', { fontSize: '16px', marginTop: '8px', marginBottom: '0px' });
			assignStyles('.pil-product .pil-content strong', { width: '100%', fontSize: '16px', marginBottom: '8px', display: 'block' });
			assignStyles('.product-intro-length p', {
				fontSize: '18px',
				width: '100%',
				marginTop: '0px',
				textAlign: 'center',
				marginBottom: '28px',
			});
			assignStyles('.product-intro-length .pil-products .pil-product .pil-content span', {
				width: '100%',
				textTransform: 'uppercase',
				fontWeight: 'bold',
				display: 'block',
			});
			assignStyles('article', {
				width: '30%',
				display: 'inline-block',
				marginRight: '1%',
				marginBottom: '36px',
				verticalAlign: 'top',
			});
			assignStyles('article .featured-img', {
				height: '128px',
				width: '100%',
				backgroundSize: 'cover',
				float: 'left',
				marginBottom: '18px',
			});

			root.querySelectorAll('.wpd-image img').forEach((img) => {
				img.src = img.src.replace('//', 'https://');
				Object.assign(img.style, { width: 'auto', maxHeight: '78px' });
			});
			root.querySelectorAll('.product-intro-length .pil-products .pil-product .pil-content img').forEach((img) => {
				img.src = img.src.replace('//', 'https://');
				Object.assign(img.style, { width: 'auto', maxHeight: '78px', display: 'inline-block' });
			});
			root.querySelectorAll('article .featured-img').forEach((el) => {
				if (el.getAttribute('style')) {
					el.setAttribute('style', el.getAttribute('style').replace('//', 'https://'));
				}
			});
			root.querySelectorAll('article h3 a').forEach((link) => {
				link.href = link.href.replace('/blogs/', 'https://www.peterdobias.com/blogs/');
			});
			root.querySelectorAll('a').forEach((link) => {
				const utm = 'utm_source=Healthy%20Dog%20Tool&utm_medium=email&utm_campaign=Healthy%20Dog%20Tool&utm_term=Delivery%20Email';

				if (!link.getAttribute('href')) return;

				link.href = link.href.indexOf('?') === -1 ? `${link.href}?${utm}` : `${link.href}&${utm}`;
			});
		}

		sendPlanEmail() {
			const emailTpl = this.dashboardEl.querySelector('#emailtpl');
			const heading = this.dashboardEl.querySelector('.product-intro-wrapper .product-intro-heading h3')?.textContent || '';
			const dogname = emailTpl?.getAttribute('data-dog-name') || '';
			const username = emailTpl?.getAttribute('data-user-name') || '';
			let emailHtml = `<h3 class="emailheading">${heading}</h3>`;

			this.dashboardEl.querySelectorAll('.week-dosage-single').forEach((week) => {
				emailHtml += week.outerHTML;
			});

			const remainder = this.dashboardEl.querySelector('.remainder-wrap');
			const length = this.dashboardEl.querySelector('.product-intro-length');

			if (remainder) emailHtml += remainder.outerHTML;
			if (length) emailHtml += length.outerHTML;

			emailTpl.innerHTML = emailHtml;
			emailTpl.querySelectorAll('.wpd-product-info').forEach((info) => {
				const title = info.querySelector('h4');
				const productLink = info.getAttribute('data-product-link');

				if (title && productLink) {
					title.innerHTML = `<a href="https://peterdobias.com${productLink}">${title.innerHTML}</a>`;
				}
			});
			emailTpl.querySelectorAll('.next-dosage-product, .dosage-week').forEach((el) => el.remove());
			this.applyEmailInlineStyles(emailTpl);

			const sendHtml = emailTpl.innerHTML;

			try {
				const lsEmail = JSON.parse(localStorage.getItem('pd_user_details')).emailaddress;

				if (!window.emailjs) return;

				emailjs.init(EMAILJS_PUBLIC_KEY);
				emailjs
					.send('default_service', 'template_0y41n0h', {
						nume_client: username,
						dog_name: dogname,
						email_plan: sendHtml,
						email_client: lsEmail,
					})
					.then(
						() => {
							const success = this.dashboardEl.querySelector('.email-success');

							if (success) success.classList.add('is-visible');
						},
						(err) => {
							throw new Error(err);
						}
					);
			} catch (error) {
				console.log(error);
			}
		}

		showCartNotice(message) {
			this.dashboardEl.querySelectorAll('.hdt-cart-notice').forEach((el) => el.remove());

			const notice = document.createElement('p');

			notice.className = 'hdt-cart-notice';
			notice.textContent = message;
			this.dashboardEl.querySelector('.pi-content-wrap')?.before(notice);
		}

		async orderProducts() {
			const goToCart = this.dashboardEl.querySelector('#go-to-cart');

			if (goToCart) goToCart.textContent = hdtT('loading', 'Loading');

			const formData = {
				sections: window.location.pathname === '/cart' ? 'cart-drawer,cart-bubble,main-cart' : 'cart-drawer,cart-bubble',
				sections_url: window.location.pathname,
				items: [],
			};
			const products = this.dosages.products || [];

			products.forEach((product) => {
				if (product.handle === 'livertune-supplement' && this.state.liverCleanseShow !== true) return;
				if (product.available === false) return;

				const variantId = Number(product.variantId);

				if (!Number.isFinite(variantId)) return;

				formData.items.push({ id: variantId, quantity: 1 });
			});

			if (formData.items.length === 0) {
				if (goToCart) goToCart.textContent = hdtT('go_to_cart', 'Go to Cart');
				this.showCartNotice(hdtT('no_available_products', 'No available products to add. Please review your selections.'));

				return;
			}

			let responseText = '';
			let respJson = null;

			try {
				const response = await fetch('/cart/add.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(formData),
				});

				responseText = await response.clone().text();
				respJson = JSON.parse(responseText);
			} catch (error) {
				console.error('HealthyDogTool: add to cart failed', error);
				if (goToCart) goToCart.textContent = hdtT('go_to_cart', 'Go to Cart');

				return;
			}

			if (respJson && respJson.status) {
				this.showCartNotice(
					hdtT('cart_add_partial_error', 'Something happened! Some of the products might have not been added. Please carefully check your cart!')
				);
				if (goToCart) goToCart.textContent = hdtT('go_to_cart', 'Go to Cart');

				return;
			}

			try {
				if (typeof getCartData === 'function') {
					await getCartData();
				}
			} finally {
				if (goToCart) goToCart.textContent = hdtT('go_to_cart', 'Go to Cart');
			}

			const addToCartEl = document.createElement('add-to-cart-button');
			const hasSections = respJson && respJson.sections && typeof respJson.sections === 'object';

			if (hasSections && typeof addToCartEl.openCartDrawer === 'function') {
				addToCartEl.openCartDrawer(responseText);
			} else if (typeof addToCartEl.updateCartData === 'function') {
				await addToCartEl.updateCartData();
				addToCartEl.openCartDrawer();
			} else {
				const drawer = (window.CartDrawer && window.CartDrawer.container) || document.getElementById('Cart-Drawer');

				if (drawer) {
					document.body.classList.add('open-cc');
					drawer.classList.add('active');
					drawer.focus();
				}
			}
		}
	}

	const boot = () => {
		const root = document.querySelector('.quizPersonalizeDashboard');

		if (!root || window.healthyDogTool) return;

		window.healthyDogTool = new HealthyDogTool(root);
	};

	// Script sits after the tool markup + JSON. Don't wait for DOMContentLoaded —
	// that fires after footer/cart-drawer parse and is what left the empty chrome up for seconds.
	boot();
}
